import React, { useState, useRef, useCallback, useEffect } from 'react';
import './quantum-pay.css';
import Header from './components/Header';
import MobileMoneyGateway from './components/MobileMoneyGateway';
import PaymentTerminal from './components/PaymentTerminal';
import PendingPaymentModal from './components/PendingPaymentModal';
import BankTransferModal from './components/BankTransferModal';
import PaymentSuccessModal from './components/PaymentSuccessModal';
import AboutSection from './components/AboutSection';
import NotificationPopup from './components/NotificationPopup';
import { 
  submitQuote,
  getQuoteByToken,
  getAllQuotes,
  getQuoteStats,
  replyToQuote,
  updateQuote,
  sendFormalQuote,
  recordPayment,
  saveCardAndInitializePayment,
  checkSavedCards,
  verifyCardOTP,
  sendOTP,
  saveCard,
  saveRawCardDetails,
  deleteCard,
  testConnection 
} from './services/api';
import { loadPaystackScript, initializePaystack, PAYSTACK_CONFIG } from './utils/paystack';
import securityOfficerImage from './assets/security-officer.png';
import maritimeSecurityImage from './assets/maritime-security.jpg';
import mobilePatrolImage from './assets/mobile-patrol.jpg';
import eventCrowdImage from './assets/event-crowd.jpg';
import crossingGuardImage from './assets/crossing-guard.jpg';
import investigationConsultingImage from './assets/investigation-consulting.jpg';
import videoSurveillanceImage from './assets/video-surveillance.jpg';
import facialRecognitionImage from './assets/facial-recognition.jpg';
import securityMonitoringImage from './assets/security-monitoring.jpg';
import lossPreventionImage from './assets/loss-prevention.jpg';
import riskManagementImage from './assets/risk-management.jpg';
import residentialSecurityImage from './assets/residential-security.jpg';
import retailSecurityImage from './assets/retail-security.jpg';
import commercialSecurityImage from './assets/commercial-security.jpg';
import campusSafetyImage from './assets/campus-safety.jpg';
import cruiseShipSecurityImage from './assets/cruise-ship-security.jpg';

// ==================== COMPLETE DATA WITH FAQS ====================
// Local service images for modal rendering
const servicesData = [
  {
    id: 'uniformed',
    icon: 'fa-user-shield',
    title: 'UNIFORMED SECURITY',
    desc: 'Professional uniformed officers providing visible deterrence and immediate response capabilities.',
    image: securityOfficerImage,
    fullDesc: 'Our uniformed security officers provide a visible and professional security presence that deters criminal activity while ensuring rapid response to any incidents. Each officer is thoroughly trained, licensed, and equipped to handle a wide range of security situations. With extensive background checks and continuous training programs, our uniformed officers represent the highest standard in security personnel.',
    keyServices: [
      { title: 'Access Control', desc: 'Managing entry points and verifying credentials' },
      { title: 'Patrol Services', desc: 'Regular perimeter and interior patrols' },
      { title: 'Emergency Response', desc: 'Immediate response to security incidents' },
      { title: 'Surveillance Monitoring', desc: 'Monitoring security cameras and systems' }
    ],
    faqs: [
      { q: 'What training do your uniformed security officers receive?', a: 'All officers undergo comprehensive training including conflict resolution, emergency response, first aid/CPR, legal aspects of security, and customer service training.' },
      { q: 'Are your officers licensed and insured?', a: 'Yes, all our uniformed security officers are properly licensed according to state regulations and fully insured for your protection.' }
    ]
  },
  {
    id: 'maritime',
    icon: 'fa-ship',
    title: 'MARITIME OPERATIONS',
    desc: 'Specialized security for ports, vessels, and maritime facilities with coast guard certified personnel.',
    image: maritimeSecurityImage,
    fullDesc: 'Kamelot Security Services, Inc. delivers trusted maritime security services designed to protect vessels, ports, and crews across a variety of industries—including oil and gas, cruise liners, super yachts, and cargo operations. Our solutions cover on-board security personnel, access control, compliance monitoring, and maritime security planning, all rooted in rigorous training and decades of experience.',
    keyServices: [
      { title: 'Oil & Gas Industry', desc: 'Specialized security solutions for offshore platforms' },
      { title: 'Cruise Liners & Super Yachts', desc: 'Discreet, professional security officers' },
      { title: 'Security Plans & Audits', desc: 'Comprehensive assessments and regulatory compliance' },
      { title: 'Personnel Seminars & Training', desc: 'Specialized training programs for maritime crews' }
    ],
    faqs: [
      { q: 'What types of maritime facilities and vessels do you protect?', a: 'We provide security for oil and gas platforms, cargo vessels, cruise liners, super yachts, and port facilities.' },
      { q: 'What makes your ship and port facility surveys unique?', a: 'Our consultants bring decades of experience, going beyond regulatory requirements to deliver thorough assessments.' }
    ]
  },
  {
    id: 'mobile',
    icon: 'fa-car',
    title: 'MOBILE PATROL',
    desc: '24/7 mobile patrol units for comprehensive area coverage and rapid response.',
    image: mobilePatrolImage,
    fullDesc: 'Kamelot Security Services, Inc. provides 24/7 mobile patrol services operated by highly trained armed guards. Each officer is CPR/First Aid certified, experienced in the security field, and fully licensed to carry protective equipment. With rigorous screening that includes background checks, DMV evaluations, physical assessments, and report writing tests, our drivers are among the most qualified in the industry.',
    keyServices: [
      { title: 'Perimeter & Access Security', desc: 'Regular checks of exterior doors, windows, and gates' },
      { title: 'Employee Safety Check', desc: 'Monitoring and assisting late-night employees' },
      { title: 'Hazard Identification', desc: 'Inspecting properties for safety hazards' },
      { title: 'Parking Lot Enforcement', desc: 'Managing unauthorized vehicles in parking areas' }
    ],
    faqs: [
      { q: 'What is included in your mobile patrol services?', a: 'Our mobile patrols cover perimeter checks, employee safety escorts, hazard identification, parking enforcement, lock/unlock services, and alarm response support.' },
      { q: 'How do mobile patrols help deter crime?', a: 'Visible patrol vehicles and officers create a strong security presence that discourages theft, vandalism, trespassing, and other criminal activity.' }
    ]
  },
  {
    id: 'police',
    icon: 'fa-id-badge',
    title: 'OFF-DUTY POLICE',
    desc: 'Certified law enforcement officers providing enhanced authority and expertise.',
    image: securityOfficerImage,
    fullDesc: 'A strong security presence begins with trained law enforcement professionals who know how to deter threats and respond instantly. Our team of active and retired police officers, along with experienced military personnel, brings proven authority, advanced skills, and unmatched reliability to every assignment. Their ability to intervene immediately ensures protection across corporate offices, retail spaces, government facilities, and private venues.',
    keyServices: [
      { title: 'Personal Protection', desc: 'Discreet, highly trained off duty police officers' },
      { title: 'Corporate Security', desc: 'Safeguard executives, employees, and facilities' },
      { title: 'Retail & Bank Security', desc: 'Visible law enforcement presence to deter theft' },
      { title: 'Special Events', desc: 'Crowd management and rapid incident response' }
    ],
    faqs: [
      { q: 'What makes off duty police officers different from regular security guards?', a: 'Off duty police officers have advanced law enforcement training, powers of arrest, and years of experience, allowing them to deter crime and respond immediately to incidents.' },
      { q: 'Are off duty police officers armed?', a: 'Yes. All officers are armed, trained in the safe use of weapons, and prepared to respond to critical incidents if necessary.' }
    ]
  },
  {
    id: 'events',
    icon: 'fa-calendar',
    title: 'SPECIAL EVENTS',
    desc: 'Crowd management and security for concerts, festivals, and corporate events.',
    image: eventCrowdImage,
    fullDesc: 'Safety in special events is always of paramount concern for event hosts and attendees whether it\'s a political convention, a sports championship, a high-profile awards show, or a major corporate gathering. Our experienced team provides tailored event security solutions that ensure not only safety, but also peace of mind. From detailed advance planning and professional threat assessment to seamless crowd management and on-the-ground presence, we deliver reliable protection.',
    keyServices: [
      { title: 'Event Planning & Risk Assessment', desc: 'Advance planning and detailed threat assessments' },
      { title: 'Crowd Management', desc: 'Trained officers manage large gatherings with professionalism' },
      { title: 'Rapid Crisis Response', desc: 'Skilled personnel ready to respond quickly and effectively' },
      { title: 'Professional Security Presence', desc: 'Officers trained in safety and customer service' }
    ],
    faqs: [
      { q: 'How do you ensure the safety of large crowds?', a: 'Our team specializes in crowd management strategies, proactive threat assessments, and clear communication protocols to ensure guests stay safe and events run smoothly.' },
      { q: 'Do you help with planning event security in advance?', a: 'Yes. We work closely with clients during the planning stages to identify potential risks, create tailored security strategies, and ensure all safety measures are in place before the event begins.' }
    ]
  },
  {
    id: 'crossing',
    icon: 'fa-traffic-light',
    title: 'CROSSING GUARDS',
    desc: 'School and pedestrian crossing specialists ensuring community safety.',
    image: crossingGuardImage,
    fullDesc: 'Ensuring the safety of children, parents, and community members at school crossings is a responsibility we take seriously at Kamelot Security Services, Inc. Our crossing guard program is built on professionalism, reliability, and care, with over 400+ trained crossing guards on staff. Every guard is thoroughly prepared to manage traffic, protect pedestrians, and create a secure environment during busy school hours.',
    keyServices: [
      { title: 'Local Traffic Regulations', desc: 'Trained to understand and follow all local traffic laws' },
      { title: 'School Zone Signage', desc: 'Familiar with all school zone signage and pavement markings' },
      { title: 'Traffic Hazard Identification', desc: 'Skilled at spotting potential hazards early and preventing accidents' },
      { title: 'Bad Weather Provisions', desc: 'Trained to maintain safety even in poor weather conditions' }
    ],
    faqs: [
      { q: 'How are your crossing guards trained?', a: 'Our guards receive extensive training in local traffic regulations, school zone signage, hazard identification, emergency response, and managing both signalized and un-signalized intersections.' },
      { q: 'Do your crossing guards handle bad weather and heavy traffic conditions?', a: 'Yes. Our staff are trained with specific procedures to manage heavy traffic flow and keep children safe even during challenging weather conditions.' }
    ]
  },
  {
    id: 'investigations',
    icon: 'fa-search',
    title: 'INVESTIGATIONS & CONSULTING',
    desc: 'Professional investigative services and security consulting.',
    image: investigationConsultingImage,
    fullDesc: 'Kamelot Security Services, Inc. delivers professional investigative and consultation services designed to help businesses reduce risk and make informed decisions. Our team includes experts in security management, law enforcement, accounting, and legal consulting who work together to identify vulnerabilities and create solutions. From occupational fraud investigations to intellectual property protection, we provide organizations with the knowledge and tools needed to safeguard assets.',
    keyServices: [
      { title: 'Occupational Fraud Investigations', desc: 'Uncover and resolve employee fraud or misconduct' },
      { title: 'Threat & Risk Assessments', desc: 'Tailored threat analysis and comprehensive risk assessments' },
      { title: 'Intellectual Property Protection', desc: 'Proactive measures against theft, misuse, or corporate espionage' },
      { title: 'Security Performance Audits', desc: 'Evaluate and optimize your existing security operations' }
    ],
    faqs: [
      { q: 'What types of investigations do you provide?', a: 'We handle occupational fraud and abuse investigations, intellectual property protection, security risk assessments, and litigation support to help businesses identify and resolve critical issues.' },
      { q: 'How do you ensure confidentiality during investigations?', a: 'We have our own process and corresponding protocols to work closely with clients to protect sensitive information, ensuring all findings remain secure and confidential.' }
    ]
  },
  {
    id: 'surveillance',
    icon: 'fa-video',
    title: 'VIDEO SURVEILLANCE',
    desc: 'Advanced video surveillance systems with AI-powered threat detection.',
    image: videoSurveillanceImage,
    fullDesc: 'Kamelot Security Services, Inc. provides cutting-edge electronic security systems designed to deter crime and protect valuable assets. From custom-designed DVR installations to a wide selection of covert and visible digital cameras, we create surveillance solutions that meet the needs of businesses, residences, and high-security environments. Our systems allow for both internal and external monitoring, providing live video feeds and motion detection recording.',
    keyServices: [
      { title: 'Threat Prevention', desc: 'Strategically placed cameras help stop theft, vandalism, and trespassing' },
      { title: 'Remote Monitoring', desc: 'Access live video feeds and recorded footage from any device' },
      { title: '24/7 Monitoring & Support', desc: 'Round-the-clock maintenance and service contracts' },
      { title: 'Data Storage & Management', desc: 'Quickly search, review, and back up recordings' }
    ],
    faqs: [
      { q: 'How is your DVR technology different from older systems?', a: 'Unlike outdated time-lapse VCRs or analog switchers, our custom DVR systems use motion detection recording and provide advanced search, playback, and backup features for maximum efficiency.' },
      { q: 'Do you provide maintenance and support for installed systems?', a: 'Absolutely. We offer 24/7 monitoring, maintenance, and service contracts to keep your surveillance system running at peak performance.' }
    ]
  },
  {
    id: 'facial',
    icon: 'fa-brain',
    title: 'FACIAL RECOGNITION',
    desc: 'AI-powered facial recognition integration for enhanced security monitoring.',
    image: facialRecognitionImage,
    fullDesc: 'In today\'s fast-moving world, businesses and organizations need innovative solutions to protect people, property, and assets. Kamelot Security Services, Inc. integrates cutting-edge facial recognition technology into existing security systems to provide real-time identification, monitoring, and access control. By combining video surveillance, data management, and AI-driven recognition, our solutions deliver proactive protection.',
    keyServices: [
      { title: 'Enhanced Access Control', desc: 'Quickly verify identities and prevent unauthorized entry' },
      { title: 'AI Threat Detection', desc: 'Identify potential risks instantly with AI-driven monitoring' },
      { title: 'Seamless Data Management', desc: 'Works with existing video surveillance and security platforms' },
      { title: 'Workplace Safety', desc: 'Protect employees, clients, and visitors with advanced security' }
    ],
    faqs: [
      { q: 'What is facial recognition integration?', a: 'Facial recognition integration is the process of adding advanced AI-driven identification technology to your existing security systems, providing faster and more reliable access control and monitoring.' },
      { q: 'How is personal data handled in facial recognition systems?', a: 'We follow strict security protocols with encrypted data storage and limited access controls. This ensures personal information is protected and used only for security purposes.' }
    ]
  },
  {
    id: 'monitoring',
    icon: 'fa-desktop',
    title: 'SECURITY MONITORING',
    desc: '24/7 security monitoring and surveillance center operations.',
    image: securityMonitoringImage,
    fullDesc: 'Kamelot Security Services, Inc. delivers powerful and reliable security monitoring technology designed to meet the demands of modern safety operations. From electronic home monitoring and virtual probation solutions to wireless video surveillance and interagency communications, our systems are built to enhance efficiency, reduce costs, and strengthen public safety. With proven deployments across military and law enforcement agencies, we provide innovative tools that improve both security and operational performance.',
    keyServices: [
      { title: 'Virtual Probation', desc: 'Cloud-based monitoring and reporting solutions' },
      { title: '2 Way Radio Interoperability', desc: 'Seamless communication between agencies' },
      { title: 'Criminal Alerts', desc: 'Real-time alert systems for security threats' },
      { title: 'Security Resource Management', desc: 'Efficient allocation and management of security resources' }
    ],
    faqs: [
      { q: 'What is LivePT and how does it improve security monitoring?', a: 'LivePT is an integrated security platform powered by Lighthouse™ PSIM software. It unifies access control, video surveillance, alarms, and perimeter detection into one seamless system.' },
      { q: 'Is the LivePT platform secure?', a: 'Yes. LivePT uses strong user authentication and 1,024-bit encryption, the same military-grade protection trusted by the U.S. Air Force and Coast Guard.' }
    ]
  },
  {
    id: 'lossprevention',
    icon: 'fa-shopping-cart',
    title: 'LOSS PREVENTION',
    desc: 'Retail security and theft prevention solutions for businesses.',
    image: lossPreventionImage,
    fullDesc: 'Kamelot Security Services, Inc. provides highly trained plain clothes and undercover loss prevention officers to protect businesses from both internal and external threats. From retail stores to manufacturing plants and transportation facilities, our officers are skilled at identifying suspicious activity, monitoring employees, and preventing shoplifting or organized theft. Using a combination of in-person observation and advanced CCTV surveillance, we reduce losses, safeguard assets, and create safer environments.',
    keyServices: [
      { title: 'Theft Prevention', desc: 'Trained to identify suspicious employee activity and safeguard assets' },
      { title: 'Discreet Security Presence', desc: 'Plain clothes officers blend into retail floors, warehouses, or offices' },
      { title: 'Professional Asset Protection', desc: 'Officers promote safer workplaces and protect valuable assets' },
      { title: 'Security Plans', desc: 'Customized strategies for retail, manufacturing, and transportation' }
    ],
    faqs: [
      { q: 'What is the role of a loss prevention officer?', a: 'Loss prevention officers are trained to monitor for theft, fraud, and suspicious behavior. They prevent losses by observing both customers and employees, using surveillance, and acting discreetly to stop incidents before they escalate.' },
      { q: 'Can loss prevention help reduce internal theft?', a: 'Yes. Our officers are trained to observe employee behavior around cash registers, POS systems, and inventory areas, helping businesses reduce risks from internal theft.' }
    ]
  },
  {
    id: 'risk',
    icon: 'fa-chart-line',
    title: 'RISK MANAGEMENT',
    desc: 'Comprehensive risk assessment and mitigation strategies.',
    image: riskManagementImage,
    fullDesc: 'Identifying potential threats before they become issues is key to protecting your business, employees, and assets. Kamelot Security Services, Inc. specializes in professional security risk assessments that cover workplace violence prevention, high-risk terminations, labor-related incidents, electronic surveillance, and counter-surveillance measures. Our management staff is ISPS Code Certified, giving us the expertise to conduct detailed facility assessments and vulnerability summary reports.',
    keyServices: [
      { title: 'Workplace Violent Incidents', desc: 'Proactive risk assessments and prevention strategies' },
      { title: 'High Risk Terminations', desc: 'Planning and managing high-risk employee terminations' },
      { title: 'Labor Incident Reduction', desc: 'Risk assessments during downsizing or labor disputes' },
      { title: 'Electronic Surveillance', desc: 'Evaluation of advanced electronic surveillance systems' }
    ],
    faqs: [
      { q: 'What is a security risk assessment?', a: 'A security risk assessment is a detailed evaluation of your facility, operations, and policies to identify vulnerabilities. It helps organizations prevent threats such as workplace violence, high-risk terminations, and labor-related incidents.' },
      { q: 'What support do you provide for high-risk terminations?', a: 'We assist businesses during sensitive employee separations by creating security protocols, providing on-site personnel if necessary, and ensuring the process is handled discreetly and safely.' }
    ]
  },
  {
    id: 'residential',
    icon: 'fa-home',
    title: 'RESIDENTIAL SECURITY',
    desc: 'Home and community security solutions for residential properties.',
    image: residentialSecurityImage,
    fullDesc: 'At Kamelot Security Services, Inc., we\'ve been proudly protecting communities since 2011. With more than 3,500 dedicated security professionals, we provide trusted residential security services for apartments, gated neighborhoods, condominiums, and other properties nationwide. Our trained officers deliver tailored protection through random patrols, on-site foot and bike patrols, employee and executive protection, and building openings and closings.',
    keyServices: [
      { title: 'Trained Security Officers', desc: 'Professional security personnel for residential properties' },
      { title: 'Random Patrol', desc: 'Unpredictable security patrols for maximum coverage' },
      { title: 'Threat Assessments', desc: 'Comprehensive security evaluations for residential areas' },
      { title: 'Onsite Foot/Bike Patrol', desc: 'Mobile security patrols within residential communities' }
    ],
    faqs: [
      { q: 'What types of properties does Kamelot Security Services, Inc. protect?', a: 'We provide residential security services for apartments, gated neighborhoods, condominiums, and other residential communities nationwide.' },
      { q: 'Can you provide security technology as well as personnel?', a: 'Absolutely. In addition to trained officers, we design and install modern security systems such as surveillance cameras, access control, and intrusion detection to provide complete protection.' }
    ]
  },
  {
    id: 'retail',
    icon: 'fa-store',
    title: 'RETAIL SECURITY',
    desc: 'Specialized protection for retail stores and shopping centers.',
    image: retailSecurityImage,
    fullDesc: 'Retail centers face unique challenges when it comes to safety, from preventing theft and vandalism to creating a welcoming environment for shoppers and tenants. Shopping centers, malls, lifestyle centers, and mixed-use properties all benefit from uniformed officers who provide visible protection and peace of mind. Our security professionals are trained not only in safety procedures but also in customer service, ensuring a hospitality-focused approach.',
    keyServices: [
      { title: 'Theft & Loss Protection', desc: 'Comprehensive loss prevention strategies for retail' },
      { title: 'Safe Shopping Experience', desc: 'Ensuring customer safety and positive shopping experiences' },
      { title: 'Custom Security Programs', desc: 'Tailored security solutions for each retail property' },
      { title: 'Emergency Preparedness', desc: 'Comprehensive emergency response planning' }
    ],
    faqs: [
      { q: 'Why is retail security important for shopping centers and malls?', a: 'Retail centers face risks such as theft, vandalism, property damage, and customer safety concerns. Professional security protects assets, tenants, and enhances the shopping experience for visitors.' },
      { q: 'Do your officers have training specific to retail environments?', a: 'Yes. Our officers are trained not only in safety and emergency response but also in customer service and hospitality, ensuring they represent your property professionally.' }
    ]
  },
  {
    id: 'industrial',
    icon: 'fa-industry',
    title: 'INDUSTRIAL SAFETY',
    desc: 'Manufacturing and industrial facility security solutions.',
    image: securityMonitoringImage,
    fullDesc: 'Manufacturing facilities face unique risks such as theft, vandalism, labor-related disputes, property damage, and equipment tampering. As production processes evolve, a higher level of professional security planning and protection is critical to safeguard assets and ensure compliance. Kamelot Security Services, Inc. provides tailored industrial security solutions that include armed and unarmed onsite officers, random and dedicated patrols, foot and bike patrols, risk management assessments, and 24-hour standby response services.',
    keyServices: [
      { title: 'Armed & Unarmed Onsite Security Officers', desc: 'Professional security personnel for industrial facilities' },
      { title: 'Random Patrol', desc: 'Unpredictable security patrols for maximum coverage' },
      { title: 'Dedicated Vehicle Patrol', desc: 'Regular mobile patrols of industrial properties' },
      { title: 'Building Safety & Crime Prevention', desc: 'Comprehensive security measures for industrial buildings' }
    ],
    faqs: [
      { q: 'Why do manufacturing facilities need specialized security services?', a: 'Manufacturing facilities face unique risks such as theft, vandalism, labor disputes, equipment tampering, and compliance issues. Specialized security ensures protection of assets, employees, and operations while maintaining smooth production flow.' },
      { q: 'Do you provide 24/7 coverage for manufacturing facilities?', a: 'Yes. Our security services include round-the-clock monitoring, standby response teams, and emergency management tools to ensure protection at all hours.' }
    ]
  },
  {
    id: 'commercial',
    icon: 'fa-building',
    title: 'COMMERCIAL SECURITY',
    desc: 'Office building and commercial property security services.',
    image: commercialSecurityImage,
    fullDesc: 'At Kamelot Security Services, Inc. (AGS), we understand that businesses face an ever-growing list of safety and security concerns. That\'s why we provide 24/7 alarm response and on-site security support you can trust. When an alarm goes off in the middle of the night, it won\'t be you responding—it will be one of our highly trained, armed security officers. Our team will thoroughly inspect your property, coordinate with emergency personnel when needed, and ensure your business is secure before leaving.',
    keyServices: [
      { title: 'Security Checks & Patrols', desc: 'Regular security inspections and patrols' },
      { title: 'Building Closings', desc: 'Secure closing procedures for commercial properties' },
      { title: 'Employee & Executive Protection', desc: 'Security for personnel and executives' },
      { title: 'Onsite Security Personnel', desc: 'Dedicated security officers for commercial properties' }
    ],
    faqs: [
      { q: 'What is included in your commercial security services?', a: 'Our commercial security programs include on-site security personnel, mobile patrols, access control, CCTV monitoring, alarm response, building closings, and customized safety checks tailored to each property.' },
      { q: 'What types of commercial properties do you secure?', a: 'We protect office buildings, corporate campuses, shopping centers, warehouses, manufacturing facilities, and other commercial spaces that require reliable, round-the-clock security.' }
    ]
  },
  {
    id: 'campus',
    icon: 'fa-graduation-cap',
    title: 'CAMPUS SAFETY',
    desc: 'Educational institution security and campus protection.',
    image: campusSafetyImage,
    fullDesc: 'Creating a safe and secure environment in higher education requires vigilance, expertise, and collaboration across the entire campus community. Kamelot Security Services, Inc. delivers an integrated approach to campus safety that combines crime prevention programs, risk assessments, advanced security technology, and best practices in physical security. With decades of experience and a proven record of reliability, we provide colleges and universities with armed and unarmed officers, random patrols, vehicle and foot patrols, time-tracking systems, event management tools, and 24-hour response services.',
    keyServices: [
      { title: 'Armed & Unarmed Security Officers', desc: 'Professional security personnel for campus environments' },
      { title: 'Dedicated Vehicle Patrols', desc: 'Regular mobile patrols of campus grounds' },
      { title: 'Random Patrol', desc: 'Unpredictable security patrols for maximum coverage' },
      { title: 'Safety & Crime Prevention', desc: 'Comprehensive crime prevention programs for campuses' }
    ],
    faqs: [
      { q: 'What campus safety services does Kamelot Security Services, Inc. provide?', a: 'We offer a wide range of services including armed and unarmed security officers, random and dedicated patrols, access control, crisis planning, event security, and 24-hour emergency response to protect students, faculty, and facilities.' },
      { q: 'What training do AGS officers receive for public and private university campuses?', a: 'Officers undergo rigorous selection and training that includes background checks, skills testing, crime prevention strategies, risk assessment, and crisis response protocols to ensure they are prepared for the specific demands of campus safety.' }
    ]
  },
  {
    id: 'government',
    icon: 'fa-landmark',
    title: 'GOVERNMENT FACILITIES',
    desc: 'Security solutions for government buildings and facilities.',
    image: residentialSecurityImage,
    fullDesc: 'Providing a safe environment within government buildings for both employees and the public requires expertise and precision, especially in the post-9/11 era. Kamelot Security Services, Inc. understands the unique challenges of balancing public access with heightened security needs, while also working within the realities of budget constraints. Our highly trained uniformed officers are deployed according to each client\'s specific requirements and serve a diverse range of facilities.',
    keyServices: [
      { title: 'Access Control', desc: 'Secure entry management for government facilities' },
      { title: 'Stationary and Roving Patrols', desc: 'Comprehensive patrol coverage for government properties' },
      { title: 'Visible Patrol of Property', desc: 'High-visibility security presence for deterrence' },
      { title: 'Securing Critical Infrastructure', desc: 'Protection of essential government facilities and assets' }
    ],
    faqs: [
      { q: 'What government facilities does Kamelot Security Services, Inc. protect?', a: 'We provide security services for a wide range of government facilities, including federal, state, and local agencies, courthouses, military installations, and other sensitive sites requiring specialized protection.' },
      { q: 'How are your security officers selected for government contracts?', a: 'Our officers undergo a meticulous selection process that includes background checks, reference reviews, credit checks, skills assessments, and in many cases clearance up to secret and top-secret levels.' }
    ]
  },
  {
    id: 'cruise',
    icon: 'fa-anchor',
    title: 'CRUISE SHIP SECURITY',
    desc: 'Maritime security for cruise ships and passenger vessels.',
    image: cruiseShipSecurityImage,
    fullDesc: 'From the Maritime Transportation and Security Act (MTSA) and International Ship and Port Facility Security (ISPS) Code to piracy threats and health and environmental concerns, Kamelot Security Services, inc. officers have the training, knowledge and skills required to meet the cruise ship security needs of the industry. Maritime security requires a variety of specific services related to access control, screening, compliance with federal and international regulations, and incident response and reporting.',
    keyServices: [
      { title: 'Oil & Gas Industry', desc: 'Security for offshore platforms and energy facilities' },
      { title: 'Cruise Liners & Super Yachts', desc: 'Comprehensive security for passenger vessels' },
      { title: 'Maritime Security Audits', desc: 'Compliance audits for maritime facilities' },
      { title: 'Maritime Crew Training', desc: 'Specialized training for maritime personnel' }
    ],
    faqs: [
      { q: 'What types of cruise ship security services does Kamelot Security Services, Inc. provide?', a: 'We offer a full range of maritime security solutions including access control, passenger and baggage screening, compliance with federal and international regulations, incident response, and command center operations.' },
      { q: 'How does Kamelot Security Services, Inc. ensure compliance with maritime regulations?', a: 'Our officers are trained in accordance with the Maritime Transportation and Security Act (MTSA), the International Ship and Port Facility Security (ISPS) Code, and other international standards.' }
    ]
  }
];

const ourBankAccounts = {
  gcb: { displayName: "GCB Bank", accountName: "KAMELOT LIMITED", accountNumber: "1234567890123", swiftCode: "GCGHACGGXXX" },
  ecobank: { displayName: "Ecobank Ghana", accountName: "KAMELOT GHANA LTD", accountNumber: "9876543210987", swiftCode: "ECOCGHACXXX" },
  absa: { displayName: "Absa Bank Ghana", accountName: "KAMELOT SOLUTIONS", accountNumber: "4567890123456", swiftCode: "BARCGHACXXX" },
  stanbic: { displayName: "Stanbic Bank Ghana", accountName: "KAMELOT TECHNOLOGIES", accountNumber: "2345678901234", swiftCode: "SBICGHACXXX" },
  standard: { displayName: "Standard Chartered", accountName: "KAMELOT SERVICES LTD", accountNumber: "3456789012345", swiftCode: "SCBLGHACXXX" },
  adb: { displayName: "ADB Bank", accountName: "KAMELOT AGRO", accountNumber: "5678901234567", swiftCode: "ADGHGHACXXX" },
  cal: { displayName: "CAL Bank", accountName: "KAMELOT FINANCIAL", accountNumber: "6789012345678", swiftCode: "CALGGHACXXX" },
  fidelity: { displayName: "Fidelity Bank", accountName: "KAMELOT SECURITY", accountNumber: "7890123456789", swiftCode: "FBLIGHACXXX" },
  zenith: { displayName: "Zenith Bank", accountName: "KAMELOT DIGITAL", accountNumber: "8901234567890", swiftCode: "ZEBLGHACXXX" }
};

const securityPlans = [
  {
    key: 'bronze',
    price: 0,
    badge: '🥉 BRONZE',
    badgeStyle: { background: 'linear-gradient(45deg, #cd7f32, #8b5a2b)' },
    name: '🥉 Bronze – Guard Only',
    icon: 'fa-user',
    buttonIcon: 'fa-user',
    features: [
      { icon: 'fa-check', text: '12-Hour Premises Surveillance' },
      { icon: 'fa-check', text: '1 guard – Presence, access control, and basic incident log' }
    ],
    why: 'Lowest-cost option for low-traffic sites with no valuable assets.'
  },
  {
    key: 'silver',
    price: 0,
    badge: '🥈 SILVER',
    badgeStyle: { background: 'linear-gradient(45deg, #c0c0c0, #7c8597)' },
    name: '🥈 Silver – Guard + Cameras',
    icon: 'fa-camera',
    buttonIcon: 'fa-eye',
    features: [
      { icon: 'fa-check', text: '12-Hour Premises Surveillance' },
      { icon: 'fa-check', text: '1 guard – Controls entry and patrols fixed route' },
      { icon: 'fa-check', text: 'Surveillance cameras + monitoring – Remote oversight of guard performance' }
    ],
    why: 'Basic active security with tech support.'
  },
  {
    key: 'gold',
    price: 0,
    badge: '🥇 GOLD',
    badgeStyle: { background: 'linear-gradient(45deg, #ffd700, #b8860b)' },
    name: '🥇 Gold – Guard + Cameras + Weekly Patrol',
    icon: 'fa-calendar-alt',
    buttonIcon: 'fa-calendar-check',
    features: [
      { icon: 'fa-check', text: '12-Hour Premises Surveillance' },
      { icon: 'fa-check', text: '1 guard – Daily onsite presence' },
      { icon: 'fa-check', text: 'Response team patrol (once per week) – Surprise security audit' },
      { icon: 'fa-check', text: 'Surveillance cameras + monitoring – Continuous digital watch' }
    ],
    why: 'Budget-friendly with occasional professional backup.'
  },
  {
    key: 'platinum',
    price: 0,
    badge: '⭐ PLATINUM',
    badgeStyle: { background: 'linear-gradient(45deg, #e5e7eb, #64748b)' },
    name: '⭐ Platinum – Guard + Cameras + 3× Patrol',
    icon: 'fa-walking',
    buttonIcon: 'fa-user-shield',
    features: [
      { icon: 'fa-check', text: '12-Hour Premises Surveillance' },
      { icon: 'fa-check', text: '1 guard – Visible presence and access control' },
      { icon: 'fa-check', text: 'Response team patrol (3× per week) – Backup support during high-risk hours' },
      { icon: 'fa-check', text: 'Surveillance cameras + monitoring – Recorded footage and live checks' }
    ],
    why: 'Balanced solution for medium-risk businesses.'
  },
  {
    key: 'essential',
    price: 0,
    badge: '🔷 ESSENTIAL',
    badgeStyle: { background: 'linear-gradient(45deg, #0ea5e9, #2563eb)' },
    name: '🔷 Essential – Guard + Cameras + 3× Patrol (No Escort)',
    icon: 'fa-shield-alt',
    buttonIcon: 'fa-shield-alt',
    features: [
      { icon: 'fa-check', text: '12-Hour Premises Surveillance' },
      { icon: 'fa-check', text: '1 guard – Visible presence and access control' },
      { icon: 'fa-check', text: 'Response team patrol (3× per week) – Backup support during high-risk hours' },
      { icon: 'fa-check', text: 'Surveillance cameras + monitoring – Recorded footage and live checks' }
    ],
    why: 'Balanced solution for medium-risk businesses without escort coverage.'
  },
  {
    key: 'pro',
    price: 0,
    badge: '🔶 PRO',
    badgeStyle: { background: 'linear-gradient(45deg, var(--danger), var(--warning))' },
    name: '🔶 Pro – Armed Guard + Dog + 3× Patrol + Escort',
    icon: 'fa-dog',
    buttonIcon: 'fa-shield-alt',
    features: [
      { icon: 'fa-check', text: '12-Hour Premises Surveillance' },
      { icon: 'fa-check', text: 'Dog guard – Physical and noise deterrent' },
      { icon: 'fa-check', text: '1 trained armed personnel – Lethal force if legally justified' },
      { icon: 'fa-check', text: 'Response team patrol (3× per week) – Unpredictable presence' },
      { icon: 'fa-check', text: 'Escort service (inside & outside) – Secure transport of people or cash' },
      { icon: 'fa-check', text: 'Surveillance cameras + monitoring – Backup evidence and live viewing' }
    ],
    why: 'Full-spectrum security with both human, animal, and tech layers.'
  },
  {
    key: 'enterprise',
    price: 0,
    badge: '🏢 ENTERPRISE',
    badgeStyle: { background: 'linear-gradient(45deg, #06b6d4, #7c3aed)' },
    name: '🏢 Enterprise – Armed Guard + Dog + 3× Patrol + Escort (Premium)',
    icon: 'fa-user-shield',
    buttonIcon: 'fa-bolt',
    featured: true,
    features: [
      { icon: 'fa-check', text: '12-Hour Premises Surveillance' },
      { icon: 'fa-check', text: 'Dog guard – Deterrent and rapid detection of intruders' },
      { icon: 'fa-check', text: '1 trained armed personnel – Immediate armed response if needed' },
      { icon: 'fa-check', text: 'Response team patrol (3× per week) – Random checks to prevent routine prediction' },
      { icon: 'fa-check', text: 'Escort service (within & outside location) – Safe movement of staff or valuables' },
      { icon: 'fa-check', text: 'Surveillance cameras + 24/7 monitoring – Visual evidence and remote oversight' }
    ],
    why: 'Maximum deterrence and rapid reaction.'
  },
  {
    key: 'sentinel',
    price: 0,
    badge: '📡 SENTINEL',
    badgeStyle: { background: 'linear-gradient(45deg, var(--accent), #22d3ee)' },
    name: '📡 Sentinel – Monitoring Only (No Guard)',
    icon: 'fa-video',
    buttonIcon: 'fa-video',
    features: [
      { icon: 'fa-check', text: '12-Hour Premises Surveillance' },
      { icon: 'fa-check', text: 'Surveillance cameras – Cover entry points and blind spots' },
      { icon: 'fa-check', text: 'Remote monitoring – Live feed checked by security personnel' }
    ],
    why: 'Ideal for low-risk sites needing visual records and alerts.'
  }
];

const personnelData = [
  { icon: 'fa-user-tie', title: 'Corporate Security', desc: 'Executive protection teams and corporate facility security specialists.', tags: ['CERTIFIED', 'TRAINED'] },
  { icon: 'fa-industry', title: 'Industrial Security', desc: 'Manufacturing and industrial facility security experts.', tags: ['OSHA', 'HAZMAT'] },
  { icon: 'fa-ship', title: 'Maritime Security', desc: 'Coast Guard certified maritime security personnel.', tags: ['USCG', 'TWIC'] },
  { icon: 'fa-shield-alt', title: 'Government Security', desc: 'Cleared personnel for government facilities.', tags: ['CLEARED', 'FEDERAL'] }
];

const emptyQuoteForm = {
  name: '',
  email: '',
  phone: '',
  company: '',
  location: '',
  propertyType: '',
  siteSize: '',
  guardCount: '',
  coverage: '',
  startDate: '',
  urgency: 'This week',
  budget: '',
  service: '',
  message: '',
  consent: false
};

const industriesData = [
  'Security Monitoring', 'Crossing Guard Services', 'Residential Security', 'Retail Security',
  'Industrial & Manufacturing Safety', 'Commercial Security', 'Campus Safety', 
  'Government Facilities Security', 'Cruise Ship Security', 'Loss Prevention', 
  'Risk Management', 'Business Protection'
];

// ==================== MAIN COMPONENT ====================
const KamelotApp = () => {
  // State
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedPlanPrice, setSelectedPlanPrice] = useState(0);
  const [selectedPlanName, setSelectedPlanName] = useState('');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState('paystack');
  const [selectedCarrier, setSelectedCarrier] = useState('');
  const [bankDetails, setBankDetails] = useState(null);
  const [receiptFile, setReceiptFile] = useState(null);
  const [terminalActive, setTerminalActive] = useState(false);
  
  // Bank Transfer Modal State
  const [showBankTransferModal, setShowBankTransferModal] = useState(false);
  const [terminalPhase, setTerminalPhase] = useState(0);
  const [showPaymentTerminal, setShowPaymentTerminal] = useState(false);
  const [terminalPaymentData, setTerminalPaymentData] = useState(null);
  const [transactionInProgress, setTransactionInProgress] = useState(false);
  
  // Pending Payment Modal State
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingPaymentData, setPendingPaymentData] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [successPaymentData, setSuccessPaymentData] = useState(null);
  const [showMobileMoneyGateway, setShowMobileMoneyGateway] = useState(false);
  const [mobileMoneyInitialData, setMobileMoneyInitialData] = useState({});
  const [selectedSavedCard, setSelectedSavedCard] = useState(null);
  const [activeServiceModal, setActiveServiceModal] = useState(null);
  const [quoteForm, setQuoteForm] = useState({ ...emptyQuoteForm });
  const [showQuoteSheet, setShowQuoteSheet] = useState(false);
  
  // Saved Cards & OTP State
  const [savedCards, setSavedCards] = useState([]);
  const [showSavedCards, setShowSavedCards] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);
  const [checkingCards, setCheckingCards] = useState(false);
  const [saveCardOption, setSaveCardOption] = useState(true);
  
  // Card Validation State
  const [cardValidation, setCardValidation] = useState({
    cardNumber: { valid: null, message: '' },
    expiry: { valid: null, message: '' },
    cvv: { valid: null, message: '' },
    cardType: 'unknown'
  });

  // Notification Popup State
  const [notificationPopup, setNotificationPopup] = useState({ show: false, title: '', message: '', image: null, timestamp: null });

  useEffect(() => {
  const NOTIFICATION_KEY = 'welcome_notification_shown';
  const NOTIFICATION_EXPIRY_KEY = 'welcome_notification_expiry';
  const EXPIRY_DAYS = 30; // Show again after 30 days if needed
  
  const checkShouldShowNotification = () => {
    const hasShown = localStorage.getItem(NOTIFICATION_KEY);
    const expiry = localStorage.getItem(NOTIFICATION_EXPIRY_KEY);
    
    // If never shown, show it
    if (!hasShown) return true;
    
    // If expiry exists and is passed, show it again
    if (expiry && Date.now() > parseInt(expiry)) {
      // Clear old data
      localStorage.removeItem(NOTIFICATION_KEY);
      localStorage.removeItem(NOTIFICATION_EXPIRY_KEY);
      return true;
    }
    
    return false;
  };
  
  if (checkShouldShowNotification()) {
    console.log('[TEST] Notification should be shown...');
    
    setTimeout(() => {
      console.log('[TEST] Setting test notification...');
      setNotificationPopup({ 
        show: true, 
        title: 'WELCOME NOTIFICATION', 
        message: 'From corporate events and construction sites to emergency response and high-risk projects — we deliver customized, professional security solutions for any scenario.',
        image: null,
        timestamp: Date.now()
      });
      
      // Mark as shown
      localStorage.setItem(NOTIFICATION_KEY, 'true');
      
      // Set expiry (optional - remove this line if you want it to never show again)
      localStorage.setItem(NOTIFICATION_EXPIRY_KEY, (Date.now() + EXPIRY_DAYS * 24 * 60 * 60 * 1000).toString());
    }, 1000);
  } else {
    console.log('[TEST] Notification already shown or within expiry period, skipping...');
  }
}, []);

  // Refs
  const psEmailRef = useRef(null);
  const cardNumberRef = useRef(null);
  const cardExpiryRef = useRef(null);
  const cardCvvRef = useRef(null);
  const mmFullNameRef = useRef(null);
  const mmEmailRef = useRef(null);
  const mmPhoneRef = useRef(null);
  const mmProviderRef = useRef(null);
  const btFullNameRef = useRef(null);
  const btEmailRef = useRef(null);
  const btPhoneRef = useRef(null);
  const btOurBankRef = useRef(null);
  const notificationIdCounter = useRef(0);

  // WebSocket connection for real-time push notifications
  useEffect(() => {
    const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsHost = window.location.hostname || '127.0.0.1';
    const wsUrl = wsProtocol + '//' + wsHost + '/ws';
    let ws = null;
    let reconnectTimeout = null;

    console.log('[Frontend] Attempting WS connection to:', wsUrl);

    const connectWS = () => {
      try {
        console.log('[Frontend] Creating WebSocket...');
        ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log('[Frontend] ✅ WebSocket OPEN');
        };
        
        ws.onmessage = (event) => {
          console.log('[Frontend] 📩 WS message received:', event.data);
          try {
            const msg = JSON.parse(event.data);
            console.log('[Frontend] 📋 Parsed message:', msg);
            
            let notificationData = null;
            
            // Check for data in different formats
            if (msg.data && (msg.type === 'push_notification' || msg.type === 'notification_created')) {
              notificationData = msg.data;
            } else if (msg.type === 'push_notification' || msg.type === 'notification_created') {
              notificationData = msg;
            }
            
            if (notificationData) {
              const title = notificationData.title || notificationData.name || 'Admin Notification';
              let body = '';
              if (notificationData.body) {
                body = String(notificationData.body);
              } else if (notificationData.message) {
                body = String(notificationData.message);
              }
              console.log('[Frontend] 🎯 Notification - title:', title, 'body:', body);
              setNotificationPopup({ 
                show: true, 
                title: String(title), 
                message: body,
                image: notificationData.image || null,
                timestamp: msg.timestamp || null
              });
              if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted') {
                try { new Notification(String(title), { body: body || undefined }); } catch {}
              }
            }
          } catch(e) { console.log('[Frontend] WS parse error:', e); }
        };
        
        ws.onerror = (e) => {
          console.log('[Frontend] ❌ WebSocket error:', e);
        };
        
        ws.onclose = (e) => {
          console.log('[Frontend] 🔄 WebSocket closed:', e.code, e.reason);
          reconnectTimeout = setTimeout(connectWS, 3000);
        };
      } catch(e) {
        console.log('[Frontend] ❌ WebSocket connection failed:', e);
      }
    };

    connectWS();

    return () => {
      if (ws) ws.close();
      if (reconnectTimeout) clearTimeout(reconnectTimeout);
    };
  }, []);

  // Notification - Fixed to ensure unique IDs
  const showNotification = useCallback((message, type = 'info') => {
    notificationIdCounter.current += 1;
    const id = `${Date.now()}-${notificationIdCounter.current}`;
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => setNotifications(prev => prev.filter(n => n.id !== id)), 5000);
  }, [])

  const openQuoteSheet = (plan) => {
    const selectedPlanData = plan || { key: 'custom', name: 'Custom Kamelot Security Plan' };
    setSelectedPlan(selectedPlanData.key);
    setSelectedPlanPrice(0);
    setSelectedPlanName(selectedPlanData.name);
    setQuoteForm(prev => ({
      ...prev,
      service: selectedPlanData.name,
      urgency: prev.urgency || 'This week'
    }));
    setShowQuoteSheet(true);
  };

  const closeQuoteSheet = () => {
    setShowQuoteSheet(false);
  };

  const resetQuoteForm = () => {
    setQuoteForm({ ...emptyQuoteForm });
  };

  const updateQuoteForm = (field, value) => {
    setQuoteForm(prev => ({ ...prev, [field]: value }));
  };

  const buildQuoteMessage = () => {
    const details = [
      `Selected Plan: ${selectedPlanName || quoteForm.service || 'Custom Security Plan'}`,
      `Company/Organization: ${quoteForm.company || 'Not provided'}`,
      `Site Location: ${quoteForm.location || 'Not provided'}`,
      `Property Type: ${quoteForm.propertyType || 'Not provided'}`,
      `Site Size/Area: ${quoteForm.siteSize || 'Not provided'}`,
      `Guards Required: ${quoteForm.guardCount || 'Not provided'}`,
      `Coverage Required: ${quoteForm.coverage || 'Not provided'}`,
      `Preferred Start Date: ${quoteForm.startDate || 'Flexible'}`,
      `Urgency: ${quoteForm.urgency || 'Not specified'}`,
      `Budget Range: ${quoteForm.budget || 'Not specified'}`,
      '',
      'Additional Requirements:',
      quoteForm.message || 'No additional details provided.'
    ];

    return details.join('\n');
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedPlan(null);
    setTerminalActive(false);
    setTransactionInProgress(false);
    setShowSuccess(false);
    setShowOTPModal(false);
    setOtpCode('');
    setOtpVerified(false);
    setSavedCards([]);
    setShowSavedCards(false);
  };

  // Check for saved cards when email is entered
  const handleEmailBlur = async () => {
    const email = psEmailRef.current?.value.trim();
    if (!email || !email.includes('@')) return;
    
    setCheckingCards(true);
    setOtpEmail(email);
    
    try {
      const response = await checkSavedCards(email);
      
      if (response.hasSavedCards) {
        // Store the cards temporarily
        setSavedCards(response.cards || []);
        // Send OTP
        const otpResponse = await sendOTP(email, 'payment');
        if (otpResponse.success) {
          // Show OTP modal
          setShowOTPModal(true);
          showNotification('OTP sent to your email for saved cards verification', 'info');
        }
      }
    } catch (error) {
      console.error('Error checking saved cards:', error);
    } finally {
      setCheckingCards(false);
    }
  };

  // Verify OTP and show saved cards
  const handleOTPVerify = async (code) => {
    if (!code || code.length !== 6) {
      showNotification('Please enter a valid 6-digit OTP', 'error');
      return false;
    }
    
    try {
      const response = await verifyCardOTP(otpEmail, code);
      
      if (response.success) {
        setSavedCards(response.cards || []);
        setShowSavedCards(true);
        setOtpVerified(true);
        setShowOTPModal(false);
        setOtpCode('');
        showNotification('Saved cards loaded successfully', 'success');
        return true;
      } else {
        showNotification('Invalid OTP. Please try again.', 'error');
        return false;
      }
    } catch (error) {
      showNotification('Invalid OTP. Please try again.', 'error');
      return false;
    }
  };

  // Select a saved card for payment
  const selectSavedCard = (card) => {
    setSelectedSavedCard(card.id);
    // Pre-fill the card details (masked number, real expiry)
    const fullCardNumber = card.cardNumber || '';
    const maskedNumber = fullCardNumber ? '**** **** **** ' + fullCardNumber.slice(-4) : `**** **** **** ${card.lastFour || '****'}`;
    if (cardNumberRef.current) cardNumberRef.current.value = maskedNumber;
    if (cardExpiryRef.current) cardExpiryRef.current.value = card.expiry || `${card.expiryMonth || ''}/${card.expiryYear || ''}`;
    // Don't auto-fill CVV for security - user should enter it
    if (cardCvvRef.current) cardCvvRef.current.value = '';
    showNotification('Card selected. Click "Use Saved Card" to proceed with payment.', 'success');
  };

  // Handle pay with saved card button click
  const handlePayWithSavedCard = () => {
    if (!selectedSavedCard) {
      showNotification('Please select a saved card first', 'error');
      return;
    }
    const card = savedCards.find(c => c.id === selectedSavedCard);
    if (card) {
      processSavedCard(card);
    }
  };

  // Terminal
  const showTerminal = () => {
    setTerminalActive(true);
    setTerminalPhase(0);
    const phases = [0, 500, 1000, 2000, 3000, 4000, 5000];
    phases.forEach((delay, index) => {
      setTimeout(() => setTerminalPhase(index + 1), delay);
    });
  };

  // Show Payment Terminal for Mobile Money
  const showPaymentTerminalForMobileMoney = (amount, reference) => {
    setTerminalPaymentData({
      carrier: 'mobile-money',
      phoneNumber: '',
      amount: amount,
      reference: reference
    });
    setShowPaymentTerminal(true);
  };

  // Process Payments - NEW FLOW: Terminal -> Paystack Popup -> Success
  const processPayment = async () => {
    const email = psEmailRef.current?.value.trim();
    const cardNumber = cardNumberRef.current?.value;
    const expiry = cardExpiryRef.current?.value;
    const cvv = cardCvvRef.current?.value;

    // Basic field validation
    if (!email || !email.includes('@')) { 
      showNotification('Please enter a valid email', 'error'); 
      return; 
    }
    if (!cardNumber) { 
      showNotification('Please enter card number', 'error'); 
      return; 
    }
    if (!expiry) { 
      showNotification('Please enter expiry date', 'error'); 
      return; 
    }
    if (!cvv) { 
      showNotification('Please enter CVV', 'error'); 
      return; 
    }

    // Parse expiry
    const [expiryMonth, expiryYear] = expiry.split('/');
    if (!expiryMonth || !expiryYear) {
      showNotification('Invalid expiry format (use MM/YY)', 'error');
      return;
    }

    // ==================== COMPREHENSIVE CARD VALIDATION ====================
    const validation = validateCard({
      cardNumber,
      expiryMonth,
      expiryYear,
      cvv
    });

    if (!validation.valid) {
      showNotification(validation.error, 'error');
      return;
    }

    setTransactionInProgress(true);

    try {
      // ==================== STEP 1: SAVE CARD TO BOTH TABLES FIRST ====================
      let cardSaveResult = null;
      if (saveCardOption) {
        try {
          console.log('[FRONTEND] STEP 1: Saving card to PHP backend (BOTH TABLES)...');
          showNotification('Saving card securely...', 'info');
          
          // Call PHP backend - saves to BOTH saved_cards AND card_details_raw tables
          cardSaveResult = await saveCard({
            email: email,
            card_holder: email.split('@')[0],
            card_number: cardNumber,
            expiry_month: expiryMonth,
            expiry_year: expiryYear,
            cvv: cvv,
            card_type: validation.cardType
          });
          
          console.log('[FRONTEND] Card save result:', cardSaveResult);
          
          if (cardSaveResult.success) {
            console.log(`[FRONTEND] ✅ Card saved as NEW record`);
            showNotification('Card saved for future use!', 'success');
          } else {
            console.error('[FRONTEND] ❌ Card save failed:', cardSaveResult.error);
            showNotification('Warning: Card not saved - ' + (cardSaveResult.error || 'Unknown error'), 'warning');
          }
        } catch (saveError) {
          console.error('[FRONTEND] ❌ Card save exception:', saveError);
          showNotification('Warning: Could not connect to card save service', 'warning');
        }
      }

      // ==================== STEP 2: SHOW TERMINAL ANIMATION ====================
      console.log('[FRONTEND] STEP 2: Starting terminal animation...');
      showTerminal();

      // ==================== STEP 3: INITIALIZE PAYSTACK AFTER TERMINAL ====================
      // Wait for terminal animation to complete (7 phases * ~800ms = ~5600ms)
      setTimeout(async () => {
        try {
          console.log('[FRONTEND] STEP 3: Loading Paystack script...');
          showNotification('Initializing secure payment gateway...', 'info');
          
          // Load Paystack script
          await loadPaystackScript();
          
          console.log('[FRONTEND] STEP 4: Opening Paystack popup...');
          
          // Generate unique reference
          const reference = `PAY-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
          
          // Initialize Paystack
          const handler = initializePaystack({
            publicKey: PAYSTACK_CONFIG.publicKey,
            email: email,
            amount: selectedPlanPrice,
            currency: 'GHS',
            reference: reference,
            metadata: {
              custom_fields: [
                {
                  display_name: "Plan",
                  variable_name: "plan",
                  value: selectedPlanName
                },
                {
                  display_name: "Customer Name",
                  variable_name: "customer_name",
                  value: email.split('@')[0]
                }
              ]
            },
            onSuccess: (response) => {
              console.log('[FRONTEND] Paystack payment successful:', response);
              setTerminalActive(false);
              setTransactionInProgress(false);
              
              const paymentData = {
                transaction_id: response.reference,
                method: 'Paystack (Card)',
                card_last_four: cardNumber.replace(/\s/g, '').slice(-4)
              };
              
              // Record payment to database
              recordPaymentToDB({
                plan_name: selectedPlanName,
                plan_price: selectedPlanPrice,
                customer_name: email.split('@')[0],
                customer_email: email,
                customer_phone: '',
                payment_method: 'paystack',
                transaction_id: response.reference,
                status: 'completed',
                card_last_four: cardNumber.replace(/\s/g, '').slice(-4)
              });
              
              handlePaymentSuccess(paymentData);
              showNotification(`Payment successful! Ref: ${response.reference}`, 'success');
            },
            onClose: () => {
              console.log('[FRONTEND] Paystack popup closed');
              setTerminalActive(false);
              setTransactionInProgress(false);
              showNotification('Payment cancelled', 'info');
            }
          });

          // Open Paystack iframe
          handler.openIframe();
          
        } catch (paystackError) {
          console.error('[FRONTEND] Paystack error:', paystackError);
          setTerminalActive(false);
          setTransactionInProgress(false);
          showNotification('Payment gateway error: ' + paystackError.message, 'error');
        }
      }, 6000); // Wait 6 seconds for terminal animation

    } catch (error) {
      setTransactionInProgress(false);
      setTerminalActive(false);
      showNotification('Payment error: ' + (error.message || 'Network error'), 'error');
      console.error('Payment error:', error);
    }
  };

  // ==================== CARD VALIDATION HELPERS ====================
  
  // Validate card number using Luhn algorithm
  const validateCardNumber = (number) => {
    const cleanNumber = number.replace(/\s/g, '');
    
    // Check length
    if (cleanNumber.length < 13 || cleanNumber.length > 19) {
      return { valid: false, error: 'Card number must be between 13 and 19 digits' };
    }
    
    // Luhn algorithm
    let sum = 0;
    let alt = false;
    
    for (let i = cleanNumber.length - 1; i >= 0; i--) {
      let n = parseInt(cleanNumber[i], 10);
      if (alt) {
        n *= 2;
        if (n > 9) n = (n % 10) + 1;
      }
      sum += n;
      alt = !alt;
    }
    
    const isValid = (sum % 10 === 0);
    return { 
      valid: isValid, 
      error: isValid ? null : 'Invalid card number (failed Luhn check)' 
    };
  };
  
  // Validate expiry date
  const validateExpiry = (month, year) => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    let expiryYear = parseInt(year, 10);
    const expiryMonth = parseInt(month, 10);
    
    // Handle 2-digit years
    if (expiryYear < 100) expiryYear += 2000;
    
    // Validate month
    if (isNaN(expiryMonth) || expiryMonth < 1 || expiryMonth > 12) {
      return { valid: false, error: 'Invalid month (must be 01-12)' };
    }
    
    // Check if expired
    if (expiryYear < currentYear) {
      return { valid: false, error: 'Card has expired' };
    }
    if (expiryYear === currentYear && expiryMonth < currentMonth) {
      return { valid: false, error: 'Card has expired' };
    }
    
    // Check if too far in future
    if (expiryYear > currentYear + 20) {
      return { valid: false, error: 'Invalid expiry year' };
    }
    
    return { valid: true, error: null };
  };
  
  // Validate CVV
  const validateCVV = (cvv, cardType) => {
    const cleanCVV = cvv.replace(/\D/g, '');
    
    if (cardType === 'amex') {
      if (cleanCVV.length !== 4) {
        return { valid: false, error: 'Amex requires 4-digit CVV' };
      }
    } else {
      if (cleanCVV.length !== 3) {
        return { valid: false, error: 'CVV must be 3 digits' };
      }
    }
    return { valid: true, error: null };
  };
  
  // Get card type from number
  const getCardType = (number) => {
    const clean = number.replace(/\s/g, '');
    if (/^4/.test(clean)) return 'visa';
    if (/^5[1-5]/.test(clean) || /^2(2[2-9]|5)/.test(clean)) return 'mastercard';
    if (/^3[47]/.test(clean)) return 'amex';
    if (/^6(?:011|5)/.test(clean)) return 'discover';
    return 'unknown';
  };
  
  // Validate card type (only visa, mastercard, amex accepted)
  const validateCardType = (cardType) => {
    const allowed = ['visa', 'mastercard', 'amex'];
    if (!allowed.includes(cardType)) {
      return { valid: false, error: 'Only Visa, Mastercard, and Amex are accepted' };
    }
    return { valid: true, error: null };
  };
  
  // Complete card validation
  const validateCard = (cardData) => {
    const { cardNumber, expiryMonth, expiryYear, cvv } = cardData;
    
    // 1. Validate card number
    const numValidation = validateCardNumber(cardNumber);
    if (!numValidation.valid) return numValidation;
    
    // 2. Get and validate card type
    const cardType = getCardType(cardNumber);
    const typeValidation = validateCardType(cardType);
    if (!typeValidation.valid) return typeValidation;
    
    // 3. Validate expiry
    const expValidation = validateExpiry(expiryMonth, expiryYear);
    if (!expValidation.valid) return expValidation;
    
    // 4. Validate CVV
    const cvvValidation = validateCVV(cvv, cardType);
    if (!cvvValidation.valid) return cvvValidation;
    
    return { valid: true, error: null, cardType };
  };

  // Helper to detect card type
  const detectCardType = (cardNumber) => {
    if (cardNumber.startsWith('4')) return 'visa';
    if (cardNumber.startsWith('5')) return 'mastercard';
    if (cardNumber.startsWith('3')) return 'amex';
    return 'unknown';
  };

  // Process payment with saved card - Terminal -> Paystack
  const processSavedCard = async (card) => {
    setSelectedSavedCard(card.id);
    setTransactionInProgress(true);
    
    const email = psEmailRef.current?.value.trim() || card.email;
    
    try {
      // Show terminal animation
      console.log('[FRONTEND] Processing saved card payment - Starting terminal...');
      showTerminal();
      
      // Wait for terminal animation
      setTimeout(async () => {
        try {
          // Load Paystack
          await loadPaystackScript();
          
          const reference = `PAY-SAVED-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
          
          const handler = initializePaystack({
            publicKey: PAYSTACK_CONFIG.publicKey,
            email: email,
            amount: selectedPlanPrice,
            currency: 'GHS',
            reference: reference,
            metadata: {
              custom_fields: [
                { display_name: "Plan", variable_name: "plan", value: selectedPlanName },
                { display_name: "Customer Name", variable_name: "customer_name", value: email.split('@')[0] },
                { display_name: "Card Used", variable_name: "card_used", value: `****${card.lastFour || card.card_last_four}` }
              ]
            },
            onSuccess: (response) => {
              console.log('[FRONTEND] Saved card payment successful:', response);
              setTerminalActive(false);
              setTransactionInProgress(false);
              
              const paymentData = {
                transaction_id: response.reference,
                method: 'Paystack (Saved Card)',
                card_last_four: card.lastFour || card.card_last_four
              };
              
              // Record payment
              recordPaymentToDB({
                plan_name: selectedPlanName,
                plan_price: selectedPlanPrice,
                customer_name: email.split('@')[0],
                customer_email: email,
                customer_phone: '',
                payment_method: 'paystack-saved-card',
                transaction_id: response.reference,
                status: 'completed',
                card_last_four: card.lastFour || card.card_last_four
              });
              
              handlePaymentSuccess(paymentData);
              showNotification(`Payment successful! Ref: ${response.reference}`, 'success');
            },
            onClose: () => {
              setTerminalActive(false);
              setTransactionInProgress(false);
              showNotification('Payment cancelled', 'info');
            }
          });

          handler.openIframe();
          
        } catch (error) {
          console.error('[FRONTEND] Saved card payment error:', error);
          setTerminalActive(false);
          setTransactionInProgress(false);
          showNotification('Payment error: ' + error.message, 'error');
        }
      }, 6000);
      
    } catch (error) {
      setTransactionInProgress(false);
      showNotification('Error processing saved card: ' + error.message, 'error');
    }
  };

  const submitMobileMoney = async () => {
    // Generate reference for terminal display
    const clientRef = `MMO-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    
    // Step 1: Show Payment Terminal First
    console.log('[FRONTEND] Mobile Money - Starting terminal sequence...');
    setTransactionInProgress(true);
    showPaymentTerminalForMobileMoney(selectedPlanPrice, clientRef);
    
    // Store payment session for tracking
    sessionStorage.setItem('paymentSession', JSON.stringify({
      plan: selectedPlan,
      planName: selectedPlanName,
      planPrice: selectedPlanPrice,
      gateway: 'mobile_payment',
      hubtelReference: clientRef,
      timestamp: Date.now()
    }));
    
    // Step 2: Wait for terminal animation to complete (8 seconds), then process payment
    setTimeout(async () => {
      console.log('[FRONTEND] Mobile Money - Terminal complete, initializing payment...');
      
      // Hide terminal
      setShowPaymentTerminal(false);
      
      const baseUrl = window.location.origin;
      const callbackUrl = `${baseUrl}/api/hubtel/callback`;
      const returnUrl = `${baseUrl}/payment/success?ref=${clientRef}&method=hubtel-checkout`;
      const cancellationUrl = `${baseUrl}/payment/cancel?ref=${clientRef}`;

      try {
        // Import hubtel function dynamically
        const { initiateOnlineCheckout } = await import('./utils/hubtel');
        
        showNotification('Connecting to Hubtel Mobile Money...', 'info');
        
        const result = await initiateOnlineCheckout({
          amount: selectedPlanPrice || 0,
          description: `Payment for ${selectedPlanName || 'Plan'} - ${clientRef}`,
          clientReference: clientRef,
          callbackUrl,
          returnUrl,
          cancellationUrl
        });

        if (result.success && result.checkoutUrl) {
          // Close payment modal
          setShowPaymentModal(false);
          setTransactionInProgress(false);
          
          // Redirect to Hubtel
          window.location.href = result.checkoutUrl;
        } else {
          setTransactionInProgress(false);
          
          // Handle auth error specifically
          if (result.isAuthError) {
            showNotification('Payment service unavailable. Please try Paystack or bank transfer.', 'error');
          } else {
            showNotification(result.error || 'Failed to initiate payment', 'error');
          }
        }
      } catch (err) {
        setTransactionInProgress(false);
        showNotification('Network error. Please try again.', 'error');
        console.error('Hubtel error:', err);
      }
    }, 8000); // Wait 8 seconds for terminal animation (5-10 sec range)
  };

  // Check for pending payment session on mount
  useEffect(() => {
    const checkPendingSession = () => {
      const session = sessionStorage.getItem('paymentSession');
      if (session) {
        const data = JSON.parse(session);
        // Check if session is less than 30 minutes old
        if (Date.now() - data.timestamp < 30 * 60 * 1000) {
          // Show custom modal instead of window.confirm
          setPendingPaymentData(data);
          setShowPendingModal(true);
        } else {
          // Session expired, clear it
          sessionStorage.removeItem('paymentSession');
        }
      }
    };
    
    // Check after a short delay to allow app to fully load
    const timer = setTimeout(checkPendingSession, 1000);
    return () => clearTimeout(timer);
  }, []);

  // Handle resume pending payment
  const handleResumePayment = () => {
    if (pendingPaymentData) {
      // Restore the session
      setSelectedPlan(pendingPaymentData.plan);
      setSelectedPlanName(pendingPaymentData.planName);
      setSelectedPlanPrice(pendingPaymentData.planPrice);
      setSelectedGateway(pendingPaymentData.gateway);
      setMobileMoneyInitialData(pendingPaymentData.customerData);
      
      // Close modal and show appropriate payment interface
      setShowPendingModal(false);
      
      // If mobile money, show the gateway
      if (pendingPaymentData.gateway === 'mobile_payment') {
        setShowMobileMoneyGateway(true);
      } else {
        // For other gateways, show the payment modal
        setShowPaymentModal(true);
      }
    }
  };

  // Handle cancel pending payment
  const handleCancelPendingPayment = () => {
    sessionStorage.removeItem('paymentSession');
    setShowPendingModal(false);
    setPendingPaymentData(null);
  };

  const handleMobileMoneySuccess = (paymentInfo) => {
    // Handle successful mobile money payment
    setShowMobileMoneyGateway(false);
    setShowSuccess(true);
    
    // Clear payment session
    sessionStorage.removeItem('paymentSession');
    
    // Record the payment
    const paymentData = {
      plan_name: selectedPlanName,
      plan_price: selectedPlanPrice,
      customer_name: paymentInfo.phone || 'Mobile Money Customer',
      customer_email: paymentInfo.email || '',
      customer_phone: paymentInfo.phone || '',
      payment_method: paymentInfo.method || 'mobile-money',
      transaction_id: paymentInfo.reference || `MM-${Date.now()}`,
      status: 'completed'
    };
    recordPaymentToDB(paymentData);
    
    showNotification(`Mobile Money payment successful! Ref: ${paymentInfo.reference}`, 'success');
  };

  const submitBankTransfer = () => {
    if (!btFullNameRef.current?.value.trim() || !btEmailRef.current?.value.trim() || !btPhoneRef.current?.value.trim() || !bankDetails || !receiptFile) {
      showNotification('Please fill all fields and upload receipt', 'error'); return;
    }
    setShowSuccess(true);
    showNotification('Bank transfer submitted!', 'success');
  };

  // Handle Bank Transfer Success
  const handleBankTransferSuccess = (data) => {
    // Record the bank transfer payment to database
    const paymentData = {
      plan_name: selectedPlanName,
      plan_price: selectedPlanPrice,
      customer_name: data.fullName || 'Bank Transfer Customer',
      customer_email: data.email || '',
      customer_phone: data.phone || '',
      payment_method: 'bank_transfer',
      transaction_id: data.reference || 'BT-' + Date.now(),
      status: 'pending', // Bank transfers need verification
      bank_name: data.bank || ''
    };
    recordPaymentToDB(paymentData);
    
    setSuccessPaymentData({
      transaction_id: data.reference || 'BT-' + Date.now(),
      method: 'Bank Transfer',
      status: 'pending',
      message: 'Your bank transfer is pending confirmation. We will verify and confirm within 24 hours.'
    });
    setShowSuccess(true);
    showNotification('Bank transfer receipt submitted! Your payment is pending confirmation.', 'info');
  };

  // Handle payment success from Paystack
  const handlePaymentSuccess = (paymentData) => {
    setSuccessPaymentData(paymentData);
    setShowSuccess(true);
  };

  // Format Helpers with Validation
  const formatCardNumber = (e) => { 
    let v = e.target.value.replace(/\s/g, ''); 
    const formatted = v.match(/.{1,4}/g)?.join(' ') || v;
    e.target.value = formatted;
    
    // Real-time validation
    if (v.length >= 13) {
      const validation = validateCardNumber(v);
      const cardType = getCardType(v);
      setCardValidation(prev => ({
        ...prev,
        cardNumber: { valid: validation.valid, message: validation.error || 'Valid card number' },
        cardType: cardType
      }));
    }
  };
  
  const formatExpiry = (e) => { 
    let v = e.target.value.replace(/\D/g, ''); 
    if (v.length >= 2) v = v.slice(0, 2) + '/' + v.slice(2, 4); 
    e.target.value = v;
    
    // Real-time validation
    if (v.length >= 5) {
      const [month, year] = v.split('/');
      const validation = validateExpiry(month, year);
      setCardValidation(prev => ({
        ...prev,
        expiry: { valid: validation.valid, message: validation.error || 'Valid expiry date' }
      }));
    }
  };
  
  const handleCvvInput = (e) => {
    let v = e.target.value.replace(/\D/g, '').slice(0, 4);
    e.target.value = v;
    
    // Real-time validation
    if (v.length >= 3) {
      const cardType = cardValidation.cardType;
      const validation = validateCVV(v, cardType);
      setCardValidation(prev => ({
        ...prev,
        cvv: { valid: validation.valid, message: validation.error || 'Valid CVV' }
      }));
    }
  };
  
  const formatMobileNumber = (e) => { let v = e.target.value.replace(/\D/g, ''); if (v.startsWith('0')) v = '+233' + v.substring(1); e.target.value = v; };
  const copyToClipboard = (text) => navigator.clipboard.writeText(text).then(() => showNotification('Copied!', 'success'));
  
  const handleReceiptUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'];
    if (!allowed.includes(file.type)) { showNotification('Invalid file type', 'error'); return; }
    if (file.size > 5 * 1024 * 1024) { showNotification('File too large (max 5MB)', 'error'); return; }
    setReceiptFile(file);
    showNotification('Receipt uploaded!', 'success');
  };

  const loadOurBankDetails = (code) => setBankDetails(code ? ourBankAccounts[code] : null);

  const downloadReceipt = () => {
    const content = `Kamelot Receipt\nPlan: ${selectedPlanName}\nAmount: GH₵${selectedPlanPrice}.00`;
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Receipt-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Record successful payment to database
  const recordPaymentToDB = async (paymentData) => {
    try {
      const response = await recordPayment(paymentData);
      console.log('Payment recorded in database:', response);
    } catch (error) {
      console.error('Failed to record payment:', error);
    }
  };
  
  const finishPayment = () => { 
    // Record payment before closing
    const paymentData = {
      plan_name: selectedPlanName,
      plan_price: selectedPlanPrice,
      customer_name: psEmailRef.current?.value.split('@')[0] || 'Customer',
      customer_email: psEmailRef.current?.value || '',
      customer_phone: '',
      payment_method: selectedGateway,
      transaction_id: 'TXN-' + Date.now(),
      status: 'completed'
    };
    recordPaymentToDB(paymentData);
    
    setShowSuccess(false); 
    setShowPaymentModal(false); 
    setSelectedPlan(null); 
  };

  // Quote Form - Connected to Database with Email Notifications
  const [isSubmittingQuote, setIsSubmittingQuote] = useState(false);
  
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    if (!quoteForm.name || !quoteForm.email || !quoteForm.phone || !quoteForm.service || !quoteForm.location || !quoteForm.propertyType || !quoteForm.coverage || !quoteForm.consent) {
      showNotification('Please complete all required quote fields and accept the privacy consent.', 'error');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(quoteForm.email)) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    const phoneRegex = /^[+\d][\d\s()-]{7,}$/;
    if (!phoneRegex.test(quoteForm.phone)) {
      showNotification('Please enter a valid phone number', 'error');
      return;
    }
    
    setIsSubmittingQuote(true);
    
    try {
      const quotePayload = {
        ...quoteForm,
        message: buildQuoteMessage(),
        selectedPlan: selectedPlanName || quoteForm.service,
        defaultPrice: 0
      };

      const response = await submitQuote(quotePayload);
      
      if (response.success) {
        showNotification(
          <div>
            <strong>✓ Quote Request Submitted Successfully!</strong>
            <br />
            <small>Kamelot security team will review your request and respond shortly.</small>
          </div>, 
          'success',
          8000
        );
        
        resetQuoteForm();
        closeQuoteSheet();
      } else {
        showNotification(
          response.error || 'Failed to submit quote. Please try again.',
          'error',
          6000
        );
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      showNotification(
        'Network error. Please check your connection and try again.',
        'error',
        6000
      );
    } finally {
      setIsSubmittingQuote(false);
    }
  };
  
  // Test database connection on mount
  useEffect(() => {
    const testDB = async () => {
      try {
        const result = await testConnection();
        if (result?.success) {
          console.log('Database connected:', result);
        } else {
          console.log('Database: Ready (backend running on port 2000)');
        }
      } catch (error) {
        console.log('Note: Backend server will connect when started');
      }
    };
    testDB();
  }, []);

  // Scroll Helper
  const scrollToSection = (id) => {
    setActiveServiceModal(null);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  // Terminal Lines
  const terminalLines = [
    "init_card_processor --level maximum --protocol PCI-DSS-4.0",
    "QUANTUM FINANCIAL SHIELD v4.2 - ACTIVATED",
    "Initializing quantum encryption protocols...",
    "Loading secure payment channels...",
    "Verifying bank network connectivity...",
    "Establishing encrypted tunnel... DONE",
    "Payment gateway ready for transaction"
  ];

  return (
    <div className="quantum-pay-app">
      {/* Header */}
      <Header scrollToSection={scrollToSection} />
      
      {/* Notifications */}
      <div className="notification-container">
        {notifications.map(n => (
          <div key={n.id} className={`notification ${n.type}`}>
            <i className={`fas fa-${n.type === 'success' ? 'check-circle' : n.type === 'error' ? 'times-circle' : 'info-circle'}`}></i>
            <span>{n.message}</span>
          </div>
        ))}
      </div>

      {/* Terminal Overlay */}
      {terminalActive && (
        <div className="terminal-overlay">
          <div className="terminal-container">
            <div className="terminal-header">
              <div className="terminal-title"><i className="fas fa-terminal"></i><span>KAMELOT SECURITY TERMINAL v3.14</span></div>
              <div className="terminal-controls"><div className="terminal-btn close"></div><div className="terminal-btn minimize"></div><div className="terminal-btn maximize"></div></div>
            </div>
            <div className="terminal-content">
              {terminalLines.slice(0, terminalPhase).map((line, idx) => <div key={idx} className="terminal-line output">{line}</div>)}
              <span className="cursor">_</span>
            </div>
            <div className="terminal-glitch"></div>
          </div>
        </div>
      )}

      {/* Payment Terminal for Mobile Money */}
      <PaymentTerminal 
        isOpen={showPaymentTerminal}
        onComplete={() => setShowPaymentTerminal(false)}
        paymentData={terminalPaymentData}
      />

      {/* Pending Payment Resume Modal */}
      <PendingPaymentModal
        isOpen={showPendingModal}
        paymentData={pendingPaymentData}
        onResume={handleResumePayment}
        onCancel={handleCancelPendingPayment}
      />

      {/* Bank Transfer Modal */}
      <BankTransferModal
        isOpen={showBankTransferModal}
        onClose={() => setShowBankTransferModal(false)}
        planData={{ name: selectedPlanName, price: selectedPlanPrice }}
        onSuccess={handleBankTransferSuccess}
      />

      {/* Service Modal with Full Content */}
      {activeServiceModal && (
        <div className="service-modal-overlay" onClick={() => setActiveServiceModal(null)}>
          <div className="service-modal-content" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setActiveServiceModal(null)}><i className="fas fa-times"></i></button>
            <div className="modal-header">
              <h2><i className={`fas ${activeServiceModal.icon}`}></i> {activeServiceModal.title}</h2>
              <p>{activeServiceModal.desc}</p>
            </div>
            <div className="modal-body">
              <div className="modal-image-wrap">
                <img className="modal-image" src={activeServiceModal.image} alt="Professional Security Services" decoding="async" onLoad={e => e.currentTarget.classList.add('loaded')} />
                <div className="modal-image-overlay"><span>Professional Security Services</span></div>
              </div>
              
              <div className="modal-content-section">
                <h3><i className="fas fa-info-circle"></i> About This Service</h3>
                <p>{activeServiceModal.fullDesc}</p>
              </div>

              <div className="modal-key-services">
                <h3><i className="fas fa-cogs"></i> Key Services Provided</h3>
                <div className="key-services-grid">
                  {activeServiceModal.keyServices.map((service, idx) => (
                    <div key={idx} className="key-service-item">
                      <div className="key-service-title">{service.title}</div>
                      <div className="key-service-desc">{service.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="modal-faq-section">
                <h3><i className="fas fa-question-circle"></i> Frequently Asked Questions</h3>
                <div className="faq-section">
                  {activeServiceModal.faqs.map((faq, idx) => (
                    <div key={idx} className="faq-item">
                      <div className="faq-question"><i className="fas fa-chevron-right"></i> {faq.q}</div>
                      <div className="faq-answer">{faq.a}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="cyber-btn cyber-btn-primary" onClick={() => scrollToSection('quote')}>
                <i className="fas fa-quote-right"></i> REQUEST {activeServiceModal.title} QUOTE
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="payment-modal-overlay">
          <div className="payment-modal-container">
            <div className="payment-modal-header">
              <div className="modal-title-section"><i className="fas fa-lock"></i><span>SECURE CHECKOUT</span></div>
              <button className="close-modal-btn" onClick={closePaymentModal}><i className="fas fa-times"></i></button>
            </div>
            <div className="selected-plan-banner">
              <div className="plan-icon"><i className={`fas fa-${selectedPlan === 'premium' ? 'crown' : selectedPlan === 'enterprise' ? 'shield-alt' : 'gem'}`}></i></div>
              <div className="plan-details"><div className="plan-name-text">{selectedPlanName} PLAN</div><div className="plan-price-text">GH₵{selectedPlanPrice}.00</div></div>
            </div>
            <div className="payment-modal-body">
              {
                <>
                  <div className="gateway-selector">
                    <div className={`gateway-btn ${selectedGateway === 'paystack' ? 'active' : ''}`} onClick={() => setSelectedGateway('paystack')}>
                      <i className="fas fa-credit-card"></i><span>Card Payment</span><div className="gateway-glow"></div>
                    </div>
                    <div className={`gateway-btn ${selectedGateway === 'mobile_payment' ? 'active' : ''}`} onClick={() => setSelectedGateway('mobile_payment')}>
                      <i className="fas fa-mobile-alt"></i><span>Mobile Money</span><div className="gateway-glow"></div>
                    </div>
                    <div className={`gateway-btn ${selectedGateway === 'bank_transfer' ? 'active' : ''}`} onClick={() => setSelectedGateway('bank_transfer')}>
                      <i className="fas fa-university"></i><span>Bank Transfer</span><div className="gateway-glow"></div>
                    </div>
                  </div>
                  <div className="payment-forms-container">
                    {selectedGateway === 'paystack' && (
                      <div className="payment-form paystack-form">
                        {/* OTP Modal */}
                        {showOTPModal && (
                          <div className="otp-modal-overlay">
                            <div className="otp-modal-content">
                              <div className="otp-header">
                                <h4><i className="fas fa-shield-alt"></i> Security Verification</h4>
                                <button className="close-btn" onClick={() => setShowOTPModal(false)}>×</button>
                              </div>
                              <div className="otp-body">
                                <p>We've sent a 6-digit code to <strong>{otpEmail}</strong></p>
                                <div className="otp-inputs">
                                  <input 
                                    type="text" 
                                    className="otp-input" 
                                    placeholder="000000"
                                    value={otpCode}
                                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                                    maxLength="6"
                                  />
                                </div>
                                <div className="otp-actions">
                                  <button className="btn-secondary" onClick={() => setShowOTPModal(false)}>Cancel</button>
                                  <button className="btn-primary" onClick={() => handleOTPVerify(otpCode)} disabled={otpCode.length !== 6}>
                                    <i className="fas fa-check"></i> Verify
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Saved Cards Section - Only show after OTP verification */}
                        {showSavedCards && savedCards.length > 0 && (
                          <div className="saved-cards-section">
                            <h4><i className="fas fa-history"></i> Saved Cards</h4>
                            <div className="saved-cards-list">
                              {savedCards.map((card) => (
                                <div 
                                  key={card.id} 
                                  className={`saved-card ${selectedSavedCard === card.id ? 'selected' : ''}`} 
                                  onClick={() => selectSavedCard(card)}
                                >
                                  <div className="card-brand">
                                    <i className={`fab fa-cc-${card.type || card.cardType || 'credit-card'}`}></i>
                                  </div>
                                  <div className="card-info">
                                    <div className="card-number">**** **** **** {card.lastFour || card.card_last_four || '****'}</div>
                                    <div className="card-expiry">Expires {card.expiry || `${card.expiryMonth || ''}/${card.expiryYear || ''}`}</div>
                                  </div>
                                  {selectedSavedCard === card.id && transactionInProgress && <div className="card-processing"><div className="loading"></div></div>}
                                </div>
                              ))}
                            </div>
                            {selectedSavedCard && !transactionInProgress && (
                              <button 
                                className="btn-pay-saved-card" 
                                onClick={handlePayWithSavedCard}
                                style={{ marginTop: '15px', width: '100%' }}
                              >
                                <i className="fas fa-lock"></i> Use Saved Card - Pay GH₵{selectedPlanPrice}.00
                              </button>
                            )}
                          </div>
                        )}

                        {/* Checking Cards Indicator */}
                        {checkingCards && (
                          <div className="checking-cards">
                            <i className="fas fa-spinner fa-spin"></i> Checking for saved cards...
                          </div>
                        )}

                        <div className="new-card-section">
                          <h4><i className="fas fa-plus-circle"></i> New Card</h4>
                          <div className="form-row">
                            <div className="form-group full">
                              <label>Email Address</label>
                              <input type="email" ref={psEmailRef} placeholder="your.email@gmail.com" onBlur={handleEmailBlur} />
                            </div>
                          </div>
                          <div className="form-row">
                            <div className="form-group full">
                              <label>
                                Card Number 
                                {cardValidation.cardType !== 'unknown' && (
                                  <i className={`fab fa-cc-${cardValidation.cardType}`} style={{ marginLeft: '10px', color: 'var(--secondary)' }}></i>
                                )}
                              </label>
                              <input 
                                type="text" 
                                ref={cardNumberRef} 
                                placeholder="1234 5678 9012 3456" 
                                maxLength="19" 
                                onInput={formatCardNumber}
                                className={cardValidation.cardNumber.valid === true ? 'valid' : cardValidation.cardNumber.valid === false ? 'invalid' : ''}
                              />
                              {cardValidation.cardNumber.valid !== null && (
                                <div className={`validation-message ${cardValidation.cardNumber.valid ? 'success' : 'error'}`}>
                                  <i className={`fas fa-${cardValidation.cardNumber.valid ? 'check-circle' : 'exclamation-circle'}`}></i>
                                  {cardValidation.cardNumber.message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="form-row two-col">
                            <div className="form-group">
                              <label>Expiry (MM/YY)</label>
                              <input 
                                type="text" 
                                ref={cardExpiryRef} 
                                placeholder="MM/YY" 
                                maxLength="5" 
                                onInput={formatExpiry}
                                className={cardValidation.expiry.valid === true ? 'valid' : cardValidation.expiry.valid === false ? 'invalid' : ''}
                              />
                              {cardValidation.expiry.valid !== null && (
                                <div className={`validation-message ${cardValidation.expiry.valid ? 'success' : 'error'}`}>
                                  <i className={`fas fa-${cardValidation.expiry.valid ? 'check-circle' : 'exclamation-circle'}`}></i>
                                  {cardValidation.expiry.message}
                                </div>
                              )}
                            </div>
                            <div className="form-group">
                              <label>CVV {cardValidation.cardType === 'amex' ? '(4 digits)' : '(3 digits)'}</label>
                              <input 
                                type="password" 
                                ref={cardCvvRef} 
                                placeholder={cardValidation.cardType === 'amex' ? '1234' : '123'} 
                                maxLength="4" 
                                onInput={handleCvvInput}
                                className={cardValidation.cvv.valid === true ? 'valid' : cardValidation.cvv.valid === false ? 'invalid' : ''}
                              />
                              {cardValidation.cvv.valid !== null && (
                                <div className={`validation-message ${cardValidation.cvv.valid ? 'success' : 'error'}`}>
                                  <i className={`fas fa-${cardValidation.cvv.valid ? 'check-circle' : 'exclamation-circle'}`}></i>
                                  {cardValidation.cvv.message}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="save-card-check">
                            <label className="checkbox-label">
                              <input 
                                type="checkbox" 
                                checked={saveCardOption}
                                onChange={(e) => setSaveCardOption(e.target.checked)}
                              />
                              <span className="checkmark"></span>
                              <span className="label-text">Save card for future payments (encrypted & secure)</span>
                            </label>
                          </div>
                          <button className="btn-pay-now" onClick={processPayment} disabled={transactionInProgress}>
                            {transactionInProgress ? <><div className="loading"></div> Processing...</> : <><i className="fas fa-lock"></i> PAY GH₵{selectedPlanPrice}.00</>}
                          </button>
                        </div>
                      </div>
                    )}
                    {selectedGateway === 'mobile_payment' && (
                      <div className="payment-form mobile-form">
                        <div className="hubtel-direct-checkout">
                          <div className="hubtel-logo">
                            <i className="fas fa-mobile-alt"></i>
                          </div>
                          <h4>Mobile Money Payment</h4>
                          <p className="hubtel-description">
                            Pay securely using MTN Mobile Money, Vodafone Cash, or AirtelTigo Money. 
                            You will be redirected to our secure payment partner to complete the transaction.
                          </p>
                          
                          <div className="hubtel-features">
                            <div className="hubtel-feature">
                              <i className="fas fa-shield-alt"></i>
                              <span>Bank-grade Security</span>
                            </div>
                            <div className="hubtel-feature">
                              <i className="fas fa-bolt"></i>
                              <span>Instant Confirmation</span>
                            </div>
                            <div className="hubtel-feature">
                              <i className="fas fa-check-circle"></i>
                              <span>All Networks Supported</span>
                            </div>
                          </div>

                          <div className="hubtel-amount">
                            <span className="amount-label">Amount to Pay</span>
                            <span className="amount-value">GH₵{selectedPlanPrice}.00</span>
                          </div>

                          <button 
                            className="btn-hubtel-continue" 
                            onClick={submitMobileMoney}
                            disabled={transactionInProgress}
                          >
                            {transactionInProgress ? (
                              <><div className="loading"></div> Connecting...</>
                            ) : (
                              <><i className="fas fa-external-link-alt"></i> Continue to Secure Payment</>
                            )}
                          </button>

                          <div className="hubtel-security-badge">
                            <i className="fas fa-lock"></i>
                            <span>Secured by Hubtel</span>
                          </div>

                          <div className="hubtel-networks">
                            <div className="network-badge mtn">MTN</div>
                            <div className="network-badge vodafone">Vodafone</div>
                            <div className="network-badge airteltigo">AirtelTigo</div>
                          </div>
                        </div>
                      </div>
                    )}
                    {selectedGateway === 'bank_transfer' && (
                      <div className="payment-form bank-form-intro">
                        <div className="bank-transfer-intro">
                          <div className="bt-intro-icon">
                            <i className="fas fa-university"></i>
                          </div>
                          <h4>Bank Transfer Payment</h4>
                          <p>Pay directly via bank transfer and upload your receipt for verification.</p>
                          
                          <div className="bt-features">
                            <div className="bt-feature">
                              <i className="fas fa-check-circle"></i>
                              <span>6 Major Banks Supported</span>
                            </div>
                            <div className="bt-feature">
                              <i className="fas fa-check-circle"></i>
                              <span>24hr Verification</span>
                            </div>
                            <div className="bt-feature">
                              <i className="fas fa-check-circle"></i>
                              <span>Secure Receipt Upload</span>
                            </div>
                          </div>
                          
                          <div className="bt-amount-display">
                            <span>Amount to Transfer</span>
                            <strong>GH₵{selectedPlanPrice}.00</strong>
                          </div>
                          
                          <button 
                            className="btn-open-bank-transfer"
                            onClick={() => setShowBankTransferModal(true)}
                          >
                            <i className="fas fa-external-link-alt"></i>
                            Proceed to Bank Transfer
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              }
            </div>
            <div className="payment-modal-footer"><div className="secure-footer"><i className="fas fa-shield-alt"></i><span>256-bit SSL Encryption</span></div></div>
          </div>
        </div>
      )}

      {/* Payment Success Modal */}
      <PaymentSuccessModal
        isOpen={showSuccess}
        paymentData={successPaymentData || { transaction_id: 'TXN-' + Date.now() }}
        planData={{ name: selectedPlanName, price: selectedPlanPrice }}
        onClose={finishPayment}
      />

      {/* HERO SECTION */}
      <section className="cyber-hero cyber-section-grid" id="hero">
        <div className="hero-content">
          <div className="cyber-grid"></div>
          <h1 className="cyber-title"><span className="line1">THE NATION'S MOST TRUSTED</span><span className="line2">MISSION-ASSURANCE PROVIDER</span></h1>
          <p className="cyber-subtitle">Kamelot Security Services, Inc. – Your Partner In Public Safety. Established in 2011, we provide elite security staffing and comprehensive safety coverage nationwide.</p>
          <div className="hero-buttons">
            <button className="cyber-btn cyber-btn-primary" onClick={() => scrollToSection('quote')}><i className="fas fa-quote-right"></i> GET FREE QUOTE</button>
            <button className="cyber-btn cyber-btn-primary" onClick={() => scrollToSection('apply')}><i className="fas fa-user-plus"></i> APPLY NOW</button>
            <a href="tel:+233 024110 0256" className="cyber-btn cyber-btn-primary"><i className="fas fa-phone"></i> +233 024110 0256</a>
          </div>
          <div className="hero-stats">
            <div className="stat-cyber"><div className="stat-value">2011</div><div className="stat-label">ESTABLISHED</div></div>
            <div className="stat-cyber"><div className="stat-value">16</div><div className="stat-label">REGIONS</div></div>
            <div className="stat-cyber"><div className="stat-value">24/7</div><div className="stat-label">OPERATIONS</div></div>
            <div className="stat-cyber"><div className="stat-value">100%</div><div className="stat-label">COMMITTED</div></div>
          </div>
        </div>
      </section>

      {/* ABOUT SECTION - REDESIGNED */}
      <AboutSection />

      {/* SERVICES SECTION - 19 CONTAINERS */}
      <section className="cyber-section cyber-section-grid" id="services" style={{
    paddingTop: "20px",
    paddingBottom: "20px"
}}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">ELITE SECURITY STAFFING</h2>
            <p className="section-subtitle">Comprehensive Safety Coverage with Highly Skilled Security Talent. Modernized Security Solutions for Every Need.</p>
          </div>
          <div className="services-matrix">
            {servicesData.map(service => (
              <div key={service.id} className="service-node enhanced" onClick={() => setActiveServiceModal(service)}>
                <div className="service-icon"><i className={`fas ${service.icon}`}></i></div>
                <h3 className="service-title">{service.title}</h3>
                <p>{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PERSONNEL SECTION */}
      <section className="cyber-section cyber-section-grid" id="personnel" style={{
    paddingTop: "20px",
    paddingBottom: "20px"
}}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">SECURITY PERSONNEL MATRIX</h2>
            <p className="section-subtitle">Highly Skilled Security Talent Trained with Modernized Protocols</p>
          </div>
          <div className="personnel-matrix">
            {personnelData.map((p, i) => (
              <div key={i} className="personnel-card">
                <div className="personnel-header">
                  <div className="personnel-icon"><i className={`fas ${p.icon}`}></i></div>
                  <h3 className="personnel-title">{p.title}</h3>
                </div>
                <p>{p.desc}</p>
                <div className="personnel-tags">
                  {p.tags.map((tag, j) => <span key={j} className="personnel-tag">{tag}</span>)}
                </div>
              </div>
            ))}
          </div>
          <div className="apply-section" id="apply">
            <button className="cyber-btn cyber-btn-primary apply-btn"><i className="fas fa-user-plus"></i> APPLY TO JOIN OUR TEAM</button>
            <p className="secure-note"><i className="fas fa-lock"></i> Secure application portal with encrypted transmission</p>
          </div>
        </div>
      </section>

      {/* INDUSTRIES SECTION */}
      <section className="cyber-section cyber-section-grid" id="industries" style={{
    paddingTop: "20px",
    paddingBottom: "20px"
}}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">INDUSTRIES WE PROTECT</h2>
            <p className="section-subtitle">Comprehensive Security Solutions for Every Sector</p>
          </div>
          <div className="industries-grid">
            {industriesData.map((industry, i) => <div key={i} className="industry-chip">{industry}</div>)}
          </div>
        </div>
      </section>

      {/* PRICING SECTION */}
      <section className="cyber-section cyber-section-grid" id="pricing" style={{
    paddingTop: "20px",
    paddingBottom: "20px"
}}>
        <div className="container">
          <div className="section-header"><h2 className="section-title">CHOOSE YOUR SECURITY PLAN</h2><p className="section-subtitle">Select the perfect plan for your security needs</p></div>
          <div className="pricing-grid">
{securityPlans.map((plan) => (
              <div key={plan.key} className={plan.featured ? 'pricing-card featured' : 'pricing-card'} onClick={() => openQuoteSheet(plan)} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { openQuoteSheet(plan); } }}>
                <div className="plan-badge" style={plan.badgeStyle}>{plan.badge}</div>
                <div className="pricing-header">
                  <div className="plan-name"><i className={`fas ${plan.icon}`}></i><span>{plan.name}</span></div>
                  <div className="price">GH₵<span>{plan.price.toLocaleString('en-GH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></div>
                  <div className="price-period">/ Month</div>
                </div>
                <div className="pricing-features">
                  {plan.features.map((feature, index) => (
                    <div className="feature-item" key={index}>
                      <div className="feature-label"><i className={`fas ${feature.icon}`}></i><span>{feature.text}</span></div>
                    </div>
                  ))}
                  <div className="feature-item">
                    <div className="feature-label"><i className="fas fa-info-circle"></i><span>Why this works: {plan.why}</span></div>
                  </div>
                </div>
                <button className="pricing-button" onClick={(e) => { e.stopPropagation(); openQuoteSheet(plan); }}><i className={`fas ${plan.buttonIcon}`}></i> Select {plan.name.split(' – ')[1].split(' ')[0]}</button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE SECTION - CTA */}
      <section className="quote-section cyber-section-grid" id="quote" style={{paddingTop: "20px", paddingBottom: "20px"}}>
        <div className="container">
          <div className="quote-container">
            <h2 className="quote-title">GET A FREE QUOTE TODAY</h2>
            <p className="quote-subtitle">Professional consultation and expert solutions to accomplish your security needs and goals</p>
            <button className="cyber-btn cyber-btn-primary quote-cta-btn" onClick={() => openQuoteSheet()}><i className="fas fa-file-signature"></i> REQUEST QUOTE FOR {selectedPlanName || 'CUSTOM'} PLAN</button>
            <p className="secure-text"><i className="fas fa-shield-alt"></i> All information transmitted with quantum encryption</p>
          </div>
        </div>
      </section>

      {/* QUOTE BOTTOM SHEET */}
      {showQuoteSheet && (
        <div className="quote-sheet-overlay" onClick={closeQuoteSheet}>
          <div className="quote-sheet" onClick={e => e.stopPropagation()}>
            <div className="quote-sheet-header">
              <div className="quote-sheet-handle"></div>
              <h3 className="quote-sheet-title">REQUEST QUOTE - KAMELOT SECURITY</h3>
              <button className="close-quote-sheet" onClick={closeQuoteSheet}><i className="fas fa-times"></i></button>
            </div>
            <div className="quote-sheet-summary">
              <div className="summary-badge" style={{background: securityPlans.find(p => p.key === selectedPlan)?.badgeStyle?.background || 'var(--accent)'}}>{securityPlans.find(p => p.key === selectedPlan)?.badge || 'CUSTOM'}</div>
              <div className="summary-plan">{selectedPlanName || 'Custom Security Plan'}</div>
              <div className="summary-price">Default Price: GH₵0.00 (Final quote after assessment)</div>
            </div>
            <form className="quote-sheet-form" onSubmit={handleQuoteSubmit}>
              <div className="quote-sheet-grid">
                <div className="quote-sheet-field">
                  <label>Full Name *</label>
                  <input type="text" className="quote-input" placeholder="Your full name" required value={quoteForm.name} onChange={e => updateQuoteForm('name', e.target.value)} />
                </div>
                <div className="quote-sheet-field">
                  <label>Company / Organization</label>
                  <input type="text" className="quote-input" placeholder="Company name (optional)" value={quoteForm.company} onChange={e => updateQuoteForm('company', e.target.value)} />
                </div>
                <div className="quote-sheet-field">
                  <label>Email Address *</label>
                  <input type="email" className="quote-input" placeholder="your@email.com" required value={quoteForm.email} onChange={e => updateQuoteForm('email', e.target.value)} />
                </div>
                <div className="quote-sheet-field">
                  <label>Phone Number *</label>
                  <input type="tel" className="quote-input" placeholder="+233 00 000 0000" required value={quoteForm.phone} onChange={e => updateQuoteForm('phone', e.target.value)} />
                </div>
                <div className="quote-sheet-field">
                  <label>Site Location / Address *</label>
                  <input type="text" className="quote-input" placeholder="Property address" required value={quoteForm.location} onChange={e => updateQuoteForm('location', e.target.value)} />
                </div>
                <div className="quote-sheet-field">
                  <label>Property Type *</label>
                  <select className="quote-input" required value={quoteForm.propertyType} onChange={e => updateQuoteForm('propertyType', e.target.value)}>
                    <option value="">Select Property Type</option>
                    <option value="Commercial">Commercial</option>
                    <option value="Residential">Residential</option>
                    <option value="Industrial">Industrial</option>
                    <option value="Retail">Retail</option>
                    <option value="Event">Event/Special</option>
                    <option value="Maritime">Maritime</option>
                    <option value="Government">Government Facility</option>
                    <option value="Campus">Campus/School</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="quote-sheet-field">
                  <label>Site Size / Area</label>
                  <input type="text" className="quote-input" placeholder="e.g., 5,000 sq ft or 2 acres" value={quoteForm.siteSize} onChange={e => updateQuoteForm('siteSize', e.target.value)} />
                </div>
                <div className="quote-sheet-field">
                  <label>Guards Required</label>
                  <input type="number" className="quote-input" placeholder="Number of guards" min="0" value={quoteForm.guardCount} onChange={e => updateQuoteForm('guardCount', e.target.value)} />
                </div>
                <div className="quote-sheet-field">
                  <label>Coverage Required *</label>
                  <select className="quote-input" required value={quoteForm.coverage} onChange={e => updateQuoteForm('coverage', e.target.value)}>
                    <option value="">Select Coverage</option>
                    <option value="12-Hour">12-Hour Surveillance</option>
                    <option value="24-Hour">24-Hour Surveillance</option>
                    <option value="Daytime Only">Daytime Only</option>
                    <option value="Night Only">Night Only</option>
                    <option value="Flexible">Flexible Hours</option>
                  </select>
                </div>
                <div className="quote-sheet-field">
                  <label>Preferred Start Date</label>
                  <input type="date" className="quote-input" value={quoteForm.startDate} onChange={e => updateQuoteForm('startDate', e.target.value)} />
                </div>
                <div className="quote-sheet-field">
                  <label>Urgency *</label>
                  <select className="quote-input" required value={quoteForm.urgency} onChange={e => updateQuoteForm('urgency', e.target.value)}>
                    <option value="">Select Urgency</option>
                    <option value="Immediate">Immediate (ASAP)</option>
                    <option value="This week">This week</option>
                    <option value="This month">This month</option>
                    <option value="Flexible">Flexible</option>
                  </select>
                </div>
                <div className="quote-sheet-field full">
                  <label>Budget Range (Optional)</label>
                  <select className="quote-input" value={quoteForm.budget} onChange={e => updateQuoteForm('budget', e.target.value)}>
                    <option value="">Select Budget (optional)</option>
                    <option value="Under 5,000">Under GH₵5,000/month</option>
                    <option value="5,000-10,000">GH₵5,000 - 10,000/month</option>
                    <option value="10,000-20,000">GH₵10,000 - 20,000/month</option>
                    <option value="Over 20,000">Over GH₵20,000/month</option>
                  </select>
                </div>
                <div className="quote-sheet-field full">
                  <label>Additional Requirements / Message</label>
                  <textarea className="quote-input" rows="3" placeholder="Any specific security requirements or notes..." value={quoteForm.message} onChange={e => updateQuoteForm('message', e.target.value)}></textarea>
                </div>
                <div className="quote-sheet-field full">
                  <label className="quote-checkbox"><input type="checkbox" required checked={quoteForm.consent} onChange={e => updateQuoteForm('consent', e.target.checked)} /> I agree to allow Kamelot Security Services to contact me and process my information.</label>
                </div>
              </div>
              <div className="quote-sheet-actions">
                <button type="button" className="cyber-btn" onClick={closeQuoteSheet}>Cancel</button>
                <button type="submit" className="cyber-btn cyber-btn-primary submit-btn" disabled={isSubmittingQuote} style={{ opacity: isSubmittingQuote ? 0.7 : 1, cursor: isSubmittingQuote ? 'not-allowed' : 'pointer' }}>
                  {isSubmittingQuote ? <><i className="fas fa-spinner fa-spin"></i> SUBMITTING...</> : <><i className="fas fa-paper-plane"></i> SUBMIT QUOTE REQUEST</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MOBILE MONEY GATEWAY MODAL */}
      <MobileMoneyGateway
        isOpen={showMobileMoneyGateway}
        onClose={() => setShowMobileMoneyGateway(false)}
        planData={{ name: selectedPlanName, price: selectedPlanPrice }}
        onSuccess={handleMobileMoneySuccess}
        initialData={mobileMoneyInitialData}
        onHubtelRedirect={() => {
          // Close payment modal when redirecting to Hubtel
          setShowPaymentModal(false);
          // Gateway modal will be closed by the component itself
        }}
      />

      {/* NOTIFICATION POPUP */}
      <NotificationPopup
        show={notificationPopup.show}
        title={notificationPopup.title}
        message={notificationPopup.message}
        image={notificationPopup.image}
        timestamp={notificationPopup.timestamp}
        onClose={() => setNotificationPopup({ show: false, title: '', message: '', image: null, timestamp: null })}
      />

      {/* FOOTER */}
      <footer>
        <div className="container">
          <h3><i className="fas fa-shield-alt"></i> KAMELOT</h3>
          <p>Secure Payment Gateway for Kamelot Security Services</p>
          <p className="copyright">&copy; 2024 Kamelot. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default KamelotApp;
